/* eslint-disable */
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { saveToStorage, loadFromStorage, addDrillToSession, getSessionDrills, removeDrillFromSession, clearSessionDrills } from "./Auth";
import { exportSessionPDF, exportSquadPDF, ExportButton } from "./ExportPDF";
import { useToast } from "./Auth";

/* ═══════════════════════════════════════════════════════════
   FOOTY ANALYST — SQUAD COACHING PLATFORM
   Module 1: My Squad Builder
   Module 2: Individual Development Plans
   Module 3: Match Week Training Planner
   Module 4: Opposition Preparation
═══════════════════════════════════════════════════════════ */

const SC_CSS = `
.sc-wrap { font-family: 'DM Sans', sans-serif; background: var(--cream); min-height: 100vh; }

/* HERO */
.sc-hero { background: var(--teal2); border-bottom: 3px solid var(--mustard); padding: 1.8rem; position: relative; overflow: hidden; }
.sc-hero::before { content: ''; position: absolute; inset: 0; background: repeating-linear-gradient(0deg, transparent, transparent 30px, rgba(0,0,0,0.04) 30px, rgba(0,0,0,0.04) 31px); pointer-events: none; }
.sc-hero-inner { position: relative; z-index: 1; display: flex; align-items: flex-start; justify-content: space-between; flex-wrap: wrap; gap: 1rem; }
.sc-hero-title { font-family: 'Bebas Neue', sans-serif; font-size: 2rem; letter-spacing: 3px; color: var(--white); line-height: 1; }
.sc-hero-sub { font-size: 0.75rem; color: rgba(255,255,255,0.55); font-style: italic; font-weight: 300; margin-top: 0.25rem; max-width: 480px; line-height: 1.6; }
.sc-hero-stats { display: flex; gap: 1.5rem; }
.sc-stat { text-align: center; }
.sc-stat-n { font-family: 'Bebas Neue', sans-serif; font-size: 1.6rem; letter-spacing: 2px; color: var(--mustard); line-height: 1; }
.sc-stat-l { font-size: 0.54rem; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: rgba(255,255,255,0.45); margin-top: 0.1rem; }

/* MODULE TABS */
.sc-tabs { display: flex; background: var(--white); border-bottom: 2px solid var(--rule); overflow-x: auto; }
.sc-tab { padding: 0.9rem 1.4rem; font-family: 'DM Sans', sans-serif; font-size: 0.73rem; font-weight: 800; letter-spacing: 1.5px; text-transform: uppercase; cursor: pointer; color: var(--muted); border-bottom: 3px solid transparent; margin-bottom: -2px; white-space: nowrap; transition: all 0.2s; display: flex; align-items: center; gap: 0.5rem; }
.sc-tab:hover { color: var(--teal); }
.sc-tab.on { color: var(--teal); border-bottom-color: var(--teal); }
.sc-tab-badge { font-size: 0.58rem; font-weight: 800; padding: 0.1rem 0.38rem; background: var(--mustard); color: var(--ink); letter-spacing: 1px; border-radius: 2px; }

/* CONTENT */
.sc-content { padding: 1.5rem 1.8rem; }

/* ── MODULE 1: SQUAD BUILDER ── */
.squad-layout { display: grid; grid-template-columns: 1fr 340px; gap: 1.2rem; }
.squad-pitch-wrap { background: var(--white); border: 1.5px solid var(--rule); overflow: hidden; }
.squad-pitch-header { background: var(--teal2); padding: 0.9rem 1.1rem; border-bottom: 2px solid var(--mustard); display: flex; align-items: center; justify-content: space-between; }
.squad-pitch-title { font-family: 'Bebas Neue', sans-serif; font-size: 1rem; letter-spacing: 2.5px; color: var(--white); }
.formation-select { background: transparent; border: 1px solid rgba(255,255,255,0.3); color: var(--mustard); font-family: 'DM Sans', sans-serif; font-size: 0.72rem; font-weight: 800; letter-spacing: 1px; padding: 0.28rem 0.65rem; cursor: pointer; outline: none; }
.formation-select option { background: var(--teal2); color: var(--white); }

/* Interactive pitch */
.squad-pitch { background: linear-gradient(180deg, #3a6b42 0%, #4a7c52 30%, #3d6e45 50%, #4a7c52 70%, #3a6b42 100%); position: relative; height: 420px; border: none; }
.squad-pitch svg { position: absolute; inset: 0; width: 100%; height: 100%; }
.pitch-slot { position: absolute; transform: translate(-50%,-50%); cursor: pointer; }
.pitch-slot-inner { width: 54px; height: 54px; border-radius: 50%; display: flex; flex-direction: column; align-items: center; justify-content: center; border: 2px solid rgba(255,255,255,0.4); transition: all 0.2s; }
.pitch-slot-inner:hover { transform: scale(1.1); border-color: var(--mustard); }
.pitch-slot-inner.filled { background: var(--teal); border-color: var(--mustard); }
.pitch-slot-inner.empty { background: rgba(255,255,255,0.12); border-color: rgba(255,255,255,0.35); border-style: dashed; }
.pitch-slot-pos { font-family: 'DM Sans', sans-serif; font-size: 0.52rem; font-weight: 800; color: rgba(255,255,255,0.7); letter-spacing: 1px; text-transform: uppercase; margin-bottom: 2px; }
.pitch-slot-name { font-family: 'DM Sans', sans-serif; font-size: 0.54rem; font-weight: 800; color: var(--white); text-align: center; line-height: 1.2; max-width: 48px; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
.pitch-slot-ovr { font-family: 'Bebas Neue', sans-serif; font-size: 0.75rem; letter-spacing: 1px; color: var(--mustard); }
.pitch-slot-add { font-size: 1.1rem; color: rgba(255,255,255,0.4); }

/* Squad panel */
.squad-panel { background: var(--white); border: 1.5px solid var(--rule); }
.squad-panel-header { padding: 0.85rem 1rem; border-bottom: 1.5px solid var(--rule); display: flex; align-items: center; justify-content: space-between; }
.squad-panel-title { font-family: 'Bebas Neue', sans-serif; font-size: 0.95rem; letter-spacing: 2px; color: var(--ink); }
.btn-add-player { padding: 0.38rem 0.85rem; font-family: 'DM Sans', sans-serif; font-size: 0.65rem; font-weight: 800; letter-spacing: 1.5px; text-transform: uppercase; background: var(--teal); color: var(--white); border: none; cursor: pointer; transition: all 0.2s; }
.btn-add-player:hover { background: var(--teal2); }
.squad-player-row { display: flex; align-items: center; gap: 0.6rem; padding: 0.55rem 1rem; border-bottom: 1px solid var(--rule2); cursor: pointer; transition: all 0.18s; }
.squad-player-row:hover { background: var(--teal4); }
.squad-player-row.selected { background: var(--teal4); border-left: 3px solid var(--teal); }
.sp-num { font-family: 'Bebas Neue', sans-serif; font-size: 1rem; letter-spacing: 1px; color: var(--muted); width: 22px; text-align: center; }
.sp-av { width: 30px; height: 30px; border-radius: 50%; background: var(--teal); border: 1.5px solid var(--ink); display: flex; align-items: center; justify-content: center; font-size: 0.6rem; font-weight: 800; color: var(--white); flex-shrink: 0; }
.sp-info { flex: 1; }
.sp-name { font-size: 0.82rem; font-weight: 700; color: var(--ink); }
.sp-meta { font-size: 0.62rem; color: var(--muted); font-weight: 400; }
.sp-ovr { font-family: 'Bebas Neue', sans-serif; font-size: 1.1rem; letter-spacing: 1px; color: var(--darkred); }
.sp-pos-badge { font-size: 0.54rem; font-weight: 800; padding: 0.1rem 0.38rem; letter-spacing: 1.5px; text-transform: uppercase; }
.sp-pos-badge.gk  { background: #f59e0b; color: var(--ink); }
.sp-pos-badge.def { background: var(--darkred); color: var(--white); }
.sp-pos-badge.mid { background: var(--teal); color: var(--white); }
.sp-pos-badge.att { background: #16a34a; color: var(--white); }

/* Add player modal */
.add-modal { position: fixed; inset: 0; background: rgba(26,22,18,0.92); z-index: 900; display: flex; align-items: center; justify-content: center; padding: 1.5rem; }
.add-modal-card { background: var(--white); border: 2px solid var(--ink); width: 100%; max-width: 560px; box-shadow: 6px 6px 0 var(--ink); max-height: 90vh; overflow-y: auto; }
.add-modal-header { background: var(--teal2); padding: 1.1rem 1.3rem; border-bottom: 2px solid var(--mustard); display: flex; align-items: center; justify-content: space-between; }
.add-modal-title { font-family: 'Bebas Neue', sans-serif; font-size: 1.2rem; letter-spacing: 2.5px; color: var(--white); }
.add-modal-close { background: transparent; border: 1px solid rgba(255,255,255,0.3); color: var(--white); padding: 0.28rem 0.65rem; font-family: 'DM Sans', sans-serif; font-size: 0.68rem; font-weight: 800; cursor: pointer; }
.add-modal-body { padding: 1.2rem 1.3rem; }
.add-tabs { display: flex; border: 1.5px solid var(--ink); margin-bottom: 1.2rem; }
.add-tab { flex: 1; padding: 0.55rem; text-align: center; font-family: 'DM Sans', sans-serif; font-size: 0.7rem; font-weight: 800; letter-spacing: 1.5px; text-transform: uppercase; cursor: pointer; color: var(--muted); border-right: 1px solid var(--rule); transition: all 0.2s; }
.add-tab:last-child { border-right: none; }
.add-tab.on { background: var(--teal); color: var(--white); }
.sc-label { display: block; font-size: 0.6rem; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; color: var(--muted); margin-bottom: 0.35rem; }
.sc-input { width: 100%; background: var(--cream); border: 1.5px solid var(--rule); border-bottom: 2px solid var(--ink); padding: 0.62rem 0.85rem; color: var(--ink); font-family: 'DM Sans', sans-serif; font-size: 0.85rem; font-weight: 500; outline: none; margin-bottom: 0.9rem; }
.sc-input:focus { border-bottom-color: var(--teal); }
.sc-select { width: 100%; background: var(--cream); border: 1.5px solid var(--rule); border-bottom: 2px solid var(--ink); padding: 0.62rem 0.85rem; color: var(--ink); font-family: 'DM Sans', sans-serif; font-size: 0.85rem; font-weight: 500; outline: none; margin-bottom: 0.9rem; cursor: pointer; }
.attr-sliders { display: grid; grid-template-columns: 1fr 1fr; gap: 0.6rem 1rem; margin-bottom: 1rem; }
.attr-slide { display: flex; flex-direction: column; gap: 0.25rem; }
.attr-slide-label { font-size: 0.58rem; font-weight: 800; letter-spacing: 1.5px; text-transform: uppercase; color: var(--muted); display: flex; justify-content: space-between; }
.attr-slide-val { font-family: 'Bebas Neue', sans-serif; font-size: 0.95rem; letter-spacing: 1px; }
.attr-range { width: 100%; accent-color: var(--teal); }
.btn-save-player { width: 100%; padding: 0.88rem; background: var(--teal); color: var(--white); border: 2px solid var(--teal); font-family: 'DM Sans', sans-serif; font-weight: 800; font-size: 0.8rem; letter-spacing: 2.5px; text-transform: uppercase; cursor: pointer; transition: all 0.2s; margin-top: 0.5rem; }
.btn-save-player:hover { background: var(--teal2); }

/* DB search in modal */
.db-search-modal { padding: 0 0 0.8rem; }
.db-search-input { width: 100%; background: var(--cream); border: 1.5px solid var(--rule); border-bottom: 2px solid var(--teal); padding: 0.6rem 0.85rem; color: var(--ink); font-family: 'DM Sans', sans-serif; font-size: 0.85rem; outline: none; margin-bottom: 0.7rem; }
.db-result-row { display: flex; align-items: center; gap: 0.6rem; padding: 0.52rem 0.65rem; border: 1px solid var(--rule); margin-bottom: 0.35rem; cursor: pointer; transition: all 0.18s; }
.db-result-row:hover { background: var(--teal4); border-color: var(--teal); }
.db-result-name { font-size: 0.82rem; font-weight: 700; color: var(--ink); flex: 1; }
.db-result-meta { font-size: 0.62rem; color: var(--muted); }
.db-result-ovr { font-family: 'Bebas Neue', sans-serif; font-size: 1rem; letter-spacing: 1px; color: var(--darkred); }

/* Squad analysis bar */
.squad-analysis { background: var(--white); border: 1.5px solid var(--rule); padding: 1.1rem; margin-top: 1.2rem; }
.squad-analysis-title { font-family: 'Bebas Neue', sans-serif; font-size: 0.95rem; letter-spacing: 2px; color: var(--ink); margin-bottom: 0.9rem; border-bottom: 1.5px solid var(--rule); padding-bottom: 0.65rem; display: flex; align-items: center; justify-content: space-between; }
.squad-analysis-grid { display: grid; grid-template-columns: repeat(4,1fr); gap: 0.7rem; }
.sqa-card { background: var(--cream2); border: 1px solid var(--rule); padding: 0.75rem; text-align: center; }
.sqa-val { font-family: 'Bebas Neue', sans-serif; font-size: 1.8rem; letter-spacing: 2px; color: var(--darkred); line-height: 1; }
.sqa-lbl { font-size: 0.58rem; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; color: var(--muted); margin-top: 0.25rem; }
.squad-system-match { background: var(--teal4); border: 1.5px solid var(--teal); padding: 0.85rem 1rem; margin-top: 0.9rem; display: flex; align-items: center; gap: 0.85rem; }
.ssm-pct { font-family: 'Bebas Neue', sans-serif; font-size: 2rem; letter-spacing: 2px; color: var(--teal); line-height: 1; }
.ssm-info { flex: 1; }
.ssm-system { font-size: 0.85rem; font-weight: 800; color: var(--ink); }
.ssm-reason { font-size: 0.72rem; color: var(--muted); font-style: italic; font-weight: 300; margin-top: 0.1rem; }

/* ── MODULE 2: INDIVIDUAL DEVELOPMENT ── */
.dev-layout { display: grid; grid-template-columns: 280px 1fr; gap: 1.2rem; }
.dev-player-list { background: var(--white); border: 1.5px solid var(--rule); }
.dev-player-list-header { padding: 0.85rem 1rem; border-bottom: 1.5px solid var(--rule); font-family: 'Bebas Neue', sans-serif; font-size: 0.92rem; letter-spacing: 2px; color: var(--ink); }
.dev-player-item { display: flex; align-items: center; gap: 0.6rem; padding: 0.62rem 1rem; border-bottom: 1px solid var(--rule2); cursor: pointer; transition: all 0.18s; }
.dev-player-item:hover { background: var(--teal4); }
.dev-player-item.on { background: var(--teal4); border-left: 3px solid var(--teal); }
.dev-player-detail { background: var(--white); border: 1.5px solid var(--rule); }
.dev-player-header { background: var(--teal2); padding: 1.1rem 1.3rem; border-bottom: 2px solid var(--mustard); display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; }
.dev-player-name { font-family: 'Bebas Neue', sans-serif; font-size: 1.6rem; letter-spacing: 2px; color: var(--white); line-height: 1; }
.dev-player-meta { font-size: 0.68rem; color: rgba(255,255,255,0.55); font-weight: 300; margin-top: 0.2rem; }
.dev-ovr { font-family: 'Bebas Neue', sans-serif; font-size: 3rem; letter-spacing: 2px; color: var(--mustard); line-height: 1; text-align: right; }
.dev-body { padding: 1.2rem; }
.dev-section-title { font-family: 'Bebas Neue', sans-serif; font-size: 0.88rem; letter-spacing: 2.5px; color: var(--ink); margin-bottom: 0.8rem; padding-bottom: 0.55rem; border-bottom: 1.5px solid var(--rule); display: flex; align-items: center; justify-content: space-between; }
.dev-chip { font-family: 'DM Sans', sans-serif; font-size: 0.54rem; font-weight: 800; padding: 0.14rem 0.46px; background: var(--teal); color: var(--white); letter-spacing: 2px; text-transform: uppercase; padding: 0.14rem 0.46rem; }

/* Drill cards */
.drill-card { background: var(--cream2); border: 1.5px solid var(--rule); border-left: 4px solid var(--teal); padding: 0.85rem 1rem; margin-bottom: 0.65rem; transition: all 0.2s; }
.drill-card:hover { border-left-color: var(--mustard); background: var(--teal4); }
.drill-card-title { font-size: 0.85rem; font-weight: 800; color: var(--ink); margin-bottom: 0.25rem; }
.drill-card-desc { font-size: 0.75rem; color: var(--muted); font-style: italic; font-weight: 300; line-height: 1.65; margin-bottom: 0.45rem; }
.drill-card-meta { display: flex; gap: 0.4rem; flex-wrap: wrap; }
.drill-meta-tag { font-size: 0.56rem; font-weight: 800; padding: 0.1rem 0.4rem; border: 1.5px solid; letter-spacing: 1.5px; text-transform: uppercase; }
.drill-meta-tag.duration { border-color: var(--teal); color: var(--teal); }
.drill-meta-tag.players { border-color: var(--mustard); color: var(--mustard2); }
.drill-meta-tag.intensity { border-color: var(--darkred); color: var(--darkred); }
.drill-meta-tag.focus { border-color: var(--sand); color: var(--sand2); }

/* Pro comparison */
.pro-comparison { display: grid; grid-template-columns: 1fr 1fr; gap: 0.9rem; margin-bottom: 1.1rem; }
.pro-comp-card { background: var(--cream2); border: 1.5px solid var(--rule); padding: 0.85rem; }
.pro-comp-label { font-size: 0.58rem; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; color: var(--muted); margin-bottom: 0.4rem; }
.pro-comp-name { font-family: 'Bebas Neue', sans-serif; font-size: 1.1rem; letter-spacing: 1.5px; color: var(--ink); margin-bottom: 0.4rem; }
.pro-comp-attrs { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 2px; }
.pca2 { background: var(--white); padding: 0.24rem 0.38rem; display: flex; justify-content: space-between; align-items: center; }
.pca2-l { font-size: 0.55rem; font-weight: 700; color: var(--muted); text-transform: uppercase; letter-spacing: 1px; }
.pca2-v { font-family: 'Bebas Neue', sans-serif; font-size: 0.88rem; letter-spacing: 1px; }

/* ── MODULE 3: TRAINING PLANNER ── */
.planner-controls { background: var(--white); border: 1.5px solid var(--rule); padding: 1.2rem; margin-bottom: 1.2rem; }
.planner-controls-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 1rem; margin-bottom: 1rem; }
.planner-field { display: flex; flex-direction: column; gap: 0.3rem; }
.planner-field label { font-size: 0.6rem; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; color: var(--muted); }
.planner-field select { background: var(--cream); border: 1.5px solid var(--rule); border-bottom: 2px solid var(--teal); padding: 0.6rem 0.85rem; color: var(--ink); font-family: 'DM Sans', sans-serif; font-size: 0.82rem; font-weight: 600; outline: none; cursor: pointer; }
.btn-generate { width: 100%; padding: 0.9rem; background: var(--teal); color: var(--white); border: 2px solid var(--teal); font-family: 'DM Sans', sans-serif; font-weight: 800; font-size: 0.82rem; letter-spacing: 2.5px; text-transform: uppercase; cursor: pointer; transition: all 0.2s; }
.btn-generate:hover { background: var(--teal2); border-color: var(--teal2); }

/* Match week */
.match-week { display: grid; grid-template-columns: repeat(4,1fr); gap: 1rem; }
.day-card { background: var(--white); border: 1.5px solid var(--rule); overflow: hidden; }
.day-header { padding: 0.75rem 0.9rem; border-bottom: 1.5px solid var(--rule); }
.day-name { font-family: 'Bebas Neue', sans-serif; font-size: 1rem; letter-spacing: 2.5px; color: var(--ink); }
.day-theme { font-size: 0.62rem; font-weight: 700; color: var(--teal); letter-spacing: 1px; margin-top: 0.1rem; }
.day-duration { font-size: 0.58rem; color: var(--muted); font-style: italic; }
.day-body { padding: 0.85rem 0.9rem; }
.day-section { margin-bottom: 0.85rem; }
.day-section-label { font-size: 0.58rem; font-weight: 800; letter-spacing: 2px; text-transform: uppercase; color: var(--teal); margin-bottom: 0.42rem; }
.day-drill { background: var(--teal4); border: 1px solid var(--rule); padding: 0.48rem 0.62rem; margin-bottom: 0.38rem; font-size: 0.74rem; color: var(--ink2); font-weight: 500; display: flex; align-items: center; gap: 0.4rem; }
.day-drill::before { content: '→'; color: var(--teal); font-weight: 800; flex-shrink: 0; font-size: 0.7rem; }
.day-drill-time { font-size: 0.6rem; color: var(--muted); font-style: italic; margin-left: auto; flex-shrink: 0; }
.day-intensity { display: flex; gap: 2px; margin-top: 0.6rem; }
.day-intensity-bar { flex: 1; height: 4px; border-radius: 2px; background: var(--rule2); }
.day-intensity-fill { height: 100%; border-radius: 2px; }
.day-intensity-label { font-size: 0.56rem; font-weight: 700; letter-spacing: 1px; text-transform: uppercase; color: var(--muted); margin-top: 0.3rem; }

/* Session detail */
.session-detail { background: var(--white); border: 1.5px solid var(--rule); margin-top: 1.2rem; }
.session-detail-header { background: var(--sand4); padding: 0.9rem 1.1rem; border-bottom: 1.5px solid var(--rule); }
.session-detail-title { font-family: 'Bebas Neue', sans-serif; font-size: 1rem; letter-spacing: 2px; color: var(--ink); }
.session-phase { display: flex; gap: 0.85rem; padding: 0.75rem 0; border-bottom: 1px solid var(--rule2); align-items: flex-start; }
.session-phase:last-child { border-bottom: none; }
.session-phase-time { font-family: 'Bebas Neue', sans-serif; font-size: 0.85rem; letter-spacing: 1px; color: var(--teal); width: 55px; flex-shrink: 0; margin-top: 0.1rem; }
.session-phase-content { flex: 1; }
.session-phase-title { font-size: 0.82rem; font-weight: 800; color: var(--ink); margin-bottom: 0.15rem; }
.session-phase-desc { font-size: 0.72rem; color: var(--muted); font-style: italic; font-weight: 300; line-height: 1.6; }
.session-phase-players { font-size: 0.62rem; color: var(--teal); font-weight: 700; margin-top: 0.25rem; }

/* ── MODULE 4: OPPOSITION PREP ── */
.opp-layout { display: grid; grid-template-columns: 1fr 1fr; gap: 1.2rem; }
.opp-selector { background: var(--white); border: 1.5px solid var(--rule); padding: 1.2rem; }
.opp-title { font-family: 'Bebas Neue', sans-serif; font-size: 1rem; letter-spacing: 2px; color: var(--ink); margin-bottom: 0.5rem; }
.opp-sub { font-size: 0.75rem; color: var(--muted); font-style: italic; font-weight: 300; margin-bottom: 1.1rem; line-height: 1.6; }
.opp-types { display: flex; flex-direction: column; gap: 0.5rem; margin-bottom: 1.1rem; }
.opp-type-btn { display: flex; align-items: center; gap: 0.85rem; padding: 0.78rem 0.95rem; border: 1.5px solid var(--rule); background: transparent; cursor: pointer; transition: all 0.2s; text-align: left; width: 100%; }
.opp-type-btn:hover { border-color: var(--teal); background: var(--teal4); }
.opp-type-btn.on { border-color: var(--teal); background: var(--teal4); border-left: 4px solid var(--teal); }
.opp-type-icon { font-size: 1.4rem; flex-shrink: 0; }
.opp-type-name { font-size: 0.82rem; font-weight: 800; color: var(--ink); }
.opp-type-desc { font-size: 0.68rem; color: var(--muted); font-weight: 300; font-style: italic; margin-top: 0.08rem; }
.opp-examples { font-size: 0.62rem; color: var(--teal); font-weight: 700; margin-top: 0.1rem; }
.btn-prep { width: 100%; padding: 0.88rem; background: var(--darkred); color: var(--white); border: 2px solid var(--darkred); font-family: 'DM Sans', sans-serif; font-weight: 800; font-size: 0.8rem; letter-spacing: 2.5px; text-transform: uppercase; cursor: pointer; transition: all 0.2s; }
.btn-prep:hover { background: var(--darkred2); border-color: var(--darkred2); }

/* Prep plan */
.prep-plan { display: flex; flex-direction: column; gap: 0.9rem; }
.prep-section { background: var(--white); border: 1.5px solid var(--rule); overflow: hidden; }
.prep-section-header { padding: 0.78rem 1rem; border-bottom: 1px solid var(--rule); display: flex; align-items: center; gap: 0.6rem; }
.prep-section-icon { font-size: 1.1rem; }
.prep-section-title { font-family: 'Bebas Neue', sans-serif; font-size: 0.9rem; letter-spacing: 2px; color: var(--ink); }
.prep-section-body { padding: 0.9rem 1rem; }
.prep-item { display: flex; align-items: flex-start; gap: 0.55rem; margin-bottom: 0.55rem; font-size: 0.78rem; color: var(--ink2); line-height: 1.55; font-weight: 500; }
.prep-item::before { content: '→'; color: var(--mustard); font-weight: 800; flex-shrink: 0; margin-top: 0.05rem; }
.prep-drill-item { background: var(--darkred3); border: 1px solid var(--darkred); border-left: 3px solid var(--darkred); padding: 0.62rem 0.8rem; margin-bottom: 0.45rem; }
.prep-drill-title { font-size: 0.8rem; font-weight: 800; color: var(--ink); margin-bottom: 0.15rem; }
.prep-drill-desc { font-size: 0.72rem; color: var(--ink3); font-style: italic; font-weight: 300; line-height: 1.6; }
.prep-player-focus { display: flex; align-items: center; gap: 0.6rem; padding: 0.52rem 0; border-bottom: 1px solid var(--rule2); }
.prep-player-focus:last-child { border-bottom: none; }
.prep-player-name { font-size: 0.8rem; font-weight: 700; color: var(--ink); flex: 1; }
.prep-player-task { font-size: 0.68rem; color: var(--teal); font-style: italic; font-weight: 500; }

/* EMPTY STATE */
.sc-empty { text-align: center; padding: 4rem 2rem; }
.sc-empty-icon { font-size: 3.5rem; margin-bottom: 1rem; }
.sc-empty-title { font-family: 'Bebas Neue', sans-serif; font-size: 1.8rem; letter-spacing: 2px; margin-bottom: 0.5rem; }
.sc-empty-sub { font-size: 0.85rem; color: var(--muted); font-style: italic; font-weight: 300; max-width: 380px; margin: 0 auto; line-height: 1.7; }
.btn-primary { margin-top: 1.5rem; padding: 0.88rem 2.2rem; background: var(--teal); color: var(--white); border: 2px solid var(--teal); font-family: 'DM Sans', sans-serif; font-weight: 800; font-size: 0.8rem; letter-spacing: 2.5px; text-transform: uppercase; cursor: pointer; transition: all 0.2s; }
.btn-primary:hover { background: var(--teal2); }

@media(max-width:960px) {
  .squad-layout, .dev-layout, .opp-layout { grid-template-columns: 1fr; }
  .match-week { grid-template-columns: 1fr 1fr; }
  .planner-controls-grid { grid-template-columns: 1fr; }
  .pro-comparison { grid-template-columns: 1fr; }
  .squad-analysis-grid { grid-template-columns: repeat(2,1fr); }
  .fm-attrs { grid-template-columns: repeat(3,1fr); }
}
@media(max-width:600px) {
  .squad-layout { gap:0.75rem; }
  .match-week { grid-template-columns: 1fr; }
  .squad-analysis-grid { grid-template-columns: 1fr 1fr; }
  .squad-panel-header { flex-wrap:wrap; gap:0.5rem; }
  .squad-tabs { overflow-x:auto; }
}`;

/* ═══════════════════════════════════
   COACHING DATA
═══════════════════════════════════ */

const PLAYER_STYLES = [
  "Clinical Finisher","Target Man","False 9","Pressing Striker",
  "Inverted Winger","Wide Forward","Creative Winger","Pacey Winger",
  "Box-to-Box CM","Deep Playmaker","Press Trigger CM","Creative Playmaker","Advanced Playmaker",
  "Holding Pivot","Progressive Passer","Half-space Creator",
  "Ball-playing CB","Defensive CB","Aerial CB","Sweeper CB",
  "Attacking Full-back","Defensive Full-back","Inverted Full-back","Overlapping Full-back","Wing-back",
  "Sweeper Keeper","Shot-stopper","Distribution Keeper","Commanding Keeper",
];

const PRO_PLAYER_REFS = [
  "Erling Haaland","Harry Kane","Olivier Giroud","Roberto Firmino","Karim Benzema",
  "Mohamed Salah","Bukayo Saka","Vinícius Jr.","Leroy Sané","Sadio Mané",
  "Kevin De Bruyne","Martin Ødegaard","Phil Foden","Bruno Fernandes","Gavi",
  "Rodri","Declan Rice","Fabinho","Joshua Kimmich","Sergio Busquets",
  "Virgil van Dijk","Ruben Dias","Aymeric Laporte","Rúben Dias","Lisandro Martínez",
  "Trent Alexander-Arnold","Andy Robertson","Reece James","João Cancelo","Alphonso Davies",
  "Alejandro Grimaldo","Ben White","Kyle Walker","Kieran Trippier",
  "Alisson Becker","Ederson","Manuel Neuer","Marc-André ter Stegen","David Raya",
  "Jude Bellingham","Granit Xhaka","Luka Modrić","Toni Kroos","Pedri",
];

const TACTICAL_SYSTEMS_SHORT = [
  {id:"city433",name:"Guardiola / Man City",formation:"4-3-3",style:"Positional Play"},
  {id:"liverpool423",name:"Klopp / Liverpool",formation:"4-2-3-1",style:"Counter-press"},
  {id:"arsenal433",name:"Arteta / Arsenal",formation:"4-3-3",style:"Structured Possession"},
  {id:"atletico442",name:"Simeone / Atlético",formation:"4-4-2",style:"Defensive Counter"},
  {id:"leverkusen3421",name:"Alonso / Leverkusen",formation:"3-4-2-1",style:"Possession + Press"},
  {id:"chelsea343",name:"Tuchel / Chelsea",formation:"3-4-3",style:"Wing-back Dominance"},
  {id:"bielsa4141",name:"Bielsa / Leeds",formation:"4-1-4-1",style:"Man-marking Press"},
  {id:"mourinho4231",name:"Mourinho / Inter",formation:"4-2-3-1",style:"Organised Counter"},
];

const OPPOSITION_TYPES = [
  {id:"organised",icon:"🔒",name:"Well-Organised Low Block",desc:"Sit deep, compact, frustrate and hit on the counter. Hard to break down.",examples:"Atlético Madrid, Burnley, typical bottom-half sides"},
  {id:"highpress",icon:"⚡",name:"High Press / Gegenpressing",desc:"Press high, win the ball back immediately, play vertical and direct.",examples:"Liverpool, Dortmund, Leeds — high-energy teams"},
  {id:"possession",icon:"🔄",name:"Possession-dominant Team",desc:"Control the ball, slow the game, dominate territory and tire you out.",examples:"Man City, Barcelona, Arsenal — technical sides"},
  {id:"physical",icon:"💪",name:"Physical & Direct",desc:"Long ball, second ball battles, set piece threat, physicality over technique.",examples:"Stoke City, Millwall, typical Championship teams"},
  {id:"counter",icon:"🏹",name:"Counter-attacking Team",desc:"Sit back and invite pressure, then exploit the space left when you attack.",examples:"Real Madrid, Newcastle, typically pace-based sides"},
  {id:"pressing",icon:"🎯",name:"High Press Mid-block",desc:"Press in waves from a mid-block — not as extreme as gegenpressing but active.",examples:"Brighton, Brentford, modern progressive sides"},
];

/* ── INDIVIDUAL DRILLS LIBRARY ── */
const INDIVIDUAL_DRILLS = {
  "Clinical Finisher":[
    {title:"Finishing Under Pressure",desc:"Receive the ball in the box with a defender closing you down. Must shoot within 1 second of receiving. Vary the angle and distance — left, right, close range, edge of the box.",duration:"15 min",players:"ST + GK + 2 defenders",intensity:"High",focus:"Composure"},
    {title:"Movement in Behind",desc:"Time the run in behind the defensive line as the ball is played. Practice the near-post diagonal, the far-post run and the straight run. Trigger is the pass, not the shout.",duration:"12 min",players:"ST + CM + defender",intensity:"Medium",focus:"Movement"},
    {title:"One-touch Finishing Circuit",desc:"Ball fed from different angles — near post, far post, penalty spot, edge of box. One touch to control, one touch to finish. No time to think — build instinct.",duration:"18 min",players:"ST + GK + 2 servers",intensity:"High",focus:"Technique"},
    {title:"Press Recovery Run",desc:"After the striker presses, they must recover back into their starting position. Repeat 10 times at match intensity. Builds the defensive work rate alongside the finishing quality.",duration:"10 min",players:"ST + full defensive line",intensity:"Very High",focus:"Pressing"},
  ],
  "Box-to-Box CM":[
    {title:"End-to-End Stamina Drill",desc:"CM starts at the edge of their own box. Ball is played forward — they sprint to arrive in the opposition box late. Then sprint back to defend. 8 reps with 90 second recovery.",duration:"20 min",players:"CM + servers at both ends",intensity:"Very High",focus:"Stamina"},
    {title:"Receiving Under Pressure",desc:"CM receives with a presser on their back. Must turn, play forward or lay off cleanly. Vary the body position of the presser. Build to full pace.",duration:"15 min",players:"CM + presser + forward",intensity:"High",focus:"Press resistance"},
    {title:"Late Box Arrival",desc:"CM starts 25m from the box. Ball is played wide. CM times the run to arrive at the far post as the cross comes in. Practice the timing — too early and you're marked, too late and you've missed it.",duration:"15 min",players:"CM + winger + GK",intensity:"Medium",focus:"Timing"},
    {title:"Defensive Cover Shadow",desc:"When the ball is on the right, the CM must cover the central lane — not press the ball. Practice the cover shadow position that cuts the pass inside.",duration:"12 min",players:"CM + back four",intensity:"Medium",focus:"Positioning"},
  ],
  "Inverted Winger":[
    {title:"Cut Inside & Shoot",desc:"Receive wide on the 'wrong' foot. Drive inside with 2-3 touches. Set the shot — low and across the GK to the far post. Practice from both sides. Vary the defender's starting position.",duration:"15 min",players:"W + GK + defender",intensity:"High",focus:"Dribbling & Finishing"},
    {title:"1v1 Isolation Mastery",desc:"Winger vs full-back 1v1. Winger has 8 seconds to beat the defender and deliver or shoot. Practice 3 different moves — step over, shoulder drop, inside cut. Defender is passive, then semi-active, then active.",duration:"18 min",players:"W + defender",intensity:"Medium-High",focus:"1v1 Technique"},
    {title:"Half-space Combination",desc:"Winger receives in the half-space from the CM. Plays a one-two, receives back in space and delivers or shoots. Timing of the run back is key — must be onside.",duration:"12 min",players:"W + CM + GK",intensity:"Medium",focus:"Combination play"},
    {title:"Pressing from Wide",desc:"When possession is lost, the winger presses the opposition full-back. Angle of approach cuts the inside pass. Force them wide. Repeat 10 times at match intensity.",duration:"10 min",players:"W + opposition FB",intensity:"Very High",focus:"Pressing"},
  ],
  "Ball-playing CB":[
    {title:"Build-up Under Press",desc:"3 defenders vs 2 pressers. The CBs must play through the press and find the free man. Maximum 3 touches. If they lose the ball, they do a recovery sprint.",duration:"18 min",players:"3 defenders + 2 pressers + CDM",intensity:"High",focus:"Distribution"},
    {title:"Carrying from Deep",desc:"CB receives, turns and carries the ball into midfield before releasing. Define the trigger to stop carrying — when the press activates. Practice the pass vs carry decision.",duration:"12 min",players:"CB + presser + midfield targets",intensity:"Medium",focus:"Carrying"},
    {title:"Switching Play",desc:"Ball starts on the left CB. Must switch to the right back in 2 passes. Then back the other way. Speed of execution — the ball must travel 40m in under 4 seconds.",duration:"15 min",players:"Back four + GK",intensity:"Medium",focus:"Distribution range"},
    {title:"1v1 Defending",desc:"CB vs striker in a corridor 8m wide. CB must force the striker away from goal and not allow a shot. Practice the angle of approach, the body position and the timing of the challenge.",duration:"15 min",players:"CB + striker",intensity:"High",focus:"Defending"},
  ],
  "Sweeper Keeper":[
    {title:"Sweeping Behind the Line",desc:"GK starts on their line. Server chips the ball over the defensive line. GK must sprint out to claim or clear before the striker reaches it. Vary the distance — sometimes clear, sometimes 50/50.",duration:"15 min",players:"GK + striker + server",intensity:"High",focus:"Sweeping"},
    {title:"Distribution Patterns",desc:"GK receives from the defender. Must distribute to specific targets — short to CB, medium to CDM, long to winger. Target moves to a different zone each time. GK must identify and deliver.",duration:"18 min",players:"GK + 4 field players",intensity:"Medium",focus:"Distribution"},
    {title:"Short Pass Under Press",desc:"Opposition striker presses the GK as they receive a back pass. GK must control and play short to a defender who is marked. Builds composure and decision-making under pressure.",duration:"12 min",players:"GK + defender + presser",intensity:"High",focus:"Footwork"},
    {title:"Commanding Crosses",desc:"Crosses from both sides at varied heights. GK must call and claim at the highest point. If a defender is blocking the route, GK must decide — claim or punch.",duration:"15 min",players:"GK + 2 defenders + 2 crossers",intensity:"Medium",focus:"Aerial command"},
  ],
};

/* Fallback drills for any style not listed */
const DEFAULT_DRILLS = [
  {title:"Positional Rondo",desc:"Maintain possession in a tight space against 2 defenders. Focus on movement off the ball to create passing options and press resistance.",duration:"15 min",players:"7v2",intensity:"Medium",focus:"Possession"},
  {title:"1v1 Duel Training",desc:"Isolated 1v1 duels in a grid — both attacking and defending. Vary the starting positions and the direction of attack.",duration:"12 min",players:"Pairs",intensity:"High",focus:"Individual duels"},
  {title:"Transition Game",desc:"6v6 in a compact grid. When possession changes, teams must transition immediately. Builds pressing and counter-pressing habits.",duration:"18 min",players:"12 players",intensity:"High",focus:"Transitions"},
  {title:"Position-specific Pattern",desc:"Walk through the movement patterns relevant to your position in the chosen tactical system. Add opposition progressively.",duration:"15 min",players:"Position group",intensity:"Low-Medium",focus:"Tactical"},
];

/* ── MATCH WEEK TEMPLATES ── */
const MATCH_WEEK_TEMPLATES = {
  city433:{
    tue:{theme:"Positional Play Foundation",intensity:60,drills:["Positional rondo 8v3 (20 min)","5-lane occupation shadow play (15 min)","GK build-up patterns (10 min)","Inverted full-back rotation (15 min)"],detail:"Light technical session. Focus on positional discipline and the key principles of the system. No high-intensity running.",sessions:[{time:"0-10 min",title:"Activation",desc:"Rondo 4v1 — keep it loose, lots of touches, warm up joints."},{time:"10-30 min",title:"Positional Rondo 8v3",desc:"In a 40x40 grid, 8 players vs 3 pressers. Defined positions — players must return to their zone after each pass. GK as extra option.",players:"Full squad"},{time:"30-45 min",title:"5-Lane Shadow Play",desc:"Without opposition — move the ball through all five vertical lanes in sequence. Walk the runs, then at speed.",players:"Full squad"},{time:"45-55 min",title:"Inverted Full-back",desc:"Full-backs practice tucking inside. CBs cover. Wingers go wide. Work the rotation in pairs.",players:"Defensive line + wingers"}]},
    wed:{theme:"Press Triggers & Transitions",intensity:85,drills:["Gegenpressing trigger drill (20 min)","3v2 counter-attack (15 min)","Switching play from deep (15 min)","High press activation gates (15 min)"],detail:"Highest intensity day. Press triggers are rehearsed at full pace. Transition speed is the priority.",sessions:[{time:"0-10 min",title:"Activation",desc:"Passing combinations at pace — triangle passing, overlaps, switches."},{time:"10-30 min",title:"Press Trigger Drill",desc:"Define the trigger — when the opposition CB receives, the press activates. Two teams of 11. When ball goes to CB, press team activates. If not won in 5 seconds, drop to the block.",players:"Full squad"},{time:"30-45 min",title:"3v2 Transition",desc:"After winning the ball, 3 attackers vs 2 defenders. Must score within 8 seconds. Speed of transition is the only metric.",players:"Attackers + defenders"},{time:"45-60 min",title:"Full Press Pattern",desc:"11v11 with press triggers defined. Video analysis after to review press moments.",players:"Full squad"}]},
    thu:{theme:"Set Pieces & Shape Work",intensity:50,drills:["Attacking corners (20 min)","Defensive corners (15 min)","Free kick delivery (15 min)","Shape walk-through (15 min)"],detail:"Recovery day for most. Set piece specialists work. Shape is walked through at low intensity.",sessions:[{time:"0-20 min",title:"Attacking Set Pieces",desc:"Corner routines — near post flick, far post target, penalty spot runner, second ball. Each routine 5 times.",players:"Full squad"},{time:"20-35 min",title:"Defensive Set Pieces",desc:"Zonal marking setup. Each player owns a zone. Practice the step and the claim.",players:"Full squad"},{time:"35-50 min",title:"Free Kick Delivery",desc:"Delivery practice from different positions. Taker works on both feet. Target players rehearse their runs.",players:"Set piece takers + targets"}]},
    fri:{theme:"Match Prep & Game Model",intensity:70,drills:["Opposition shape walk-through (15 min)","Attacking patterns vs opposition formation (20 min)","Set piece rehearsal (10 min)","11v11 match scenario (20 min)"],detail:"Sharpness session. Opposition shape is walked through and specific attacking patterns against it are rehearsed.",sessions:[{time:"0-15 min",title:"Opposition Analysis",desc:"Walk through the opposition formation and defensive shape. Where do they press? Where is the space? Each player must understand the plan.",players:"Full squad"},{time:"15-35 min",title:"Attacking Patterns",desc:"Practice specific attacking moves designed to exploit the opposition's defensive shape. Run each pattern 5 times.",players:"Full squad"},{time:"35-45 min",title:"Set Piece Rehearsal",desc:"Quick run-through of the set pieces you will use in the match. No coaching — just execution.",players:"Full squad"},{time:"45-60 min",title:"11v11 Scenario",desc:"Match-intensity practice game. Call specific scenarios — winning by 1, losing by 1, last 10 minutes.",players:"Full squad"}]},
  },
};

/* ── OPPOSITION PREP TEMPLATES ── */
const OPP_PREP = {
  organised:{
    title:"Breaking Down a Low Block",
    threat:"A well-organised low block will frustrate you if you play predictably. They want you to play in front of them and hit you on the counter.",
    keyPrinciples:[
      "Be patient — the goal will come if you stay disciplined and keep circulating the ball",
      "Use width to stretch their block — get the ball to the full-backs and force them wide",
      "The decisive pass is the one that goes through the block — a through-ball or a cut-back",
      "Set pieces are your best chance — invest heavily in corner and free kick routines",
      "Don't panic if it stays 0-0 at half time — this is normal against a low block",
    ],
    drills:[
      {title:"Breaking Lines Drill",desc:"6 attackers vs 6 defenders in a low block. Attackers must score by playing through the block — no going around. Forces creative passing solutions through the lines.",duration:"20 min",players:"6v6 + GK"},
      {title:"Wide Overload Rotation",desc:"Full-back and winger create a 2v1 on the wing. Force the defender to choose — then deliver the cut-back or play inside to the arriving CM.",duration:"15 min",players:"FB + W + CM + 2 defenders"},
      {title:"Second Ball Winning",desc:"Cross is delivered and cleared. Players must win the second ball and recycle quickly. Practice the shape to recover possession after a clearance.",duration:"12 min",players:"Full squad"},
      {title:"Corner Routine Rehearsal",desc:"Practice 3 different corner routines designed to score against a zonal block. Taker, target and runners must be clear on each role.",duration:"15 min",players:"Full squad"},
    ],
    playerFocusTasks:[
      "Full-backs: Overlap early and often — stretch them wide",
      "Wingers: Stay wide and hug the touchline — don't drift inside and crowd the space",
      "No.10: Find pockets between their midfield and defensive lines",
      "Striker: Hold the ball up, draw the CB out, create space for runners",
      "CMs: Arrive late into the box from deep — arrive, don't stand",
    ],
    weekPlan:[
      {day:"Tuesday",focus:"Patience and ball circulation",session:"Technical possession rondo — maximum 3 seconds to play. No forward passes under pressure."},
      {day:"Wednesday",focus:"Breaking lines",session:"Breaking Lines Drill (main session). Wide overload patterns."},
      {day:"Thursday",focus:"Set pieces",session:"Full set piece session — corners, free kicks, throw-ins in dangerous areas."},
      {day:"Friday",focus:"Match prep",session:"Walk through their shape. Rehearse the specific patterns. Short 11v11."},
    ],
  },
  highpress:{
    title:"Surviving & Exploiting a High Press",
    threat:"A high-pressing team will try to force you into mistakes in your own half. They want the ball high up the pitch where they have numbers.",
    keyPrinciples:[
      "Stay calm — a pressed player who panics gives the ball away immediately",
      "Play through the press with short, quick passes — don't go long unless it's a clear release ball",
      "The full-back is often free behind the press — a simple ball to them breaks the press immediately",
      "Once the press is broken, go quickly — the pressing team is disorganised and caught high",
      "The GK is the most important press-breaker — they must be comfortable receiving under pressure",
    ],
    drills:[
      {title:"Playing Through the Press",desc:"5 defenders + GK vs 4 pressers. Defenders must play through the press and find the full-back who is free wide. GK can always be used.",duration:"20 min",players:"5 + GK vs 4"},
      {title:"Press-breaking Combinations",desc:"Short combinations to break the press — GK to CB, CB to CDM, CDM back to CB, switch to full-back. Practice the specific patterns that your opponents' press creates.",duration:"15 min",players:"Back 5 + CDM"},
      {title:"Long Ball Target Drill",desc:"When the press can't be played through, the GK or CB hits the long ball to the striker who holds it up. Practice the target's first touch, hold and layoff.",duration:"12 min",players:"GK + CB + ST + CM arriving"},
      {title:"Transition After Breaking Press",desc:"Once the press is broken, the team must attack quickly before the opposition reorganises. 6 attackers vs 4 defenders — go in 5 seconds or less.",duration:"15 min",players:"6v4 — half pitch"},
    ],
    playerFocusTasks:[
      "GK: Must be comfortable receiving under pressure — short pass rather than long punt",
      "CBs: First option is always the GK or CDM — don't force it forward under pressure",
      "CDM: Must show for the ball constantly — be the release valve for the CBs",
      "Full-backs: Position high enough to be the press-breaker — behind the press line",
      "Striker: Hold the ball up after the long ball — buy time for the midfield to arrive",
    ],
    weekPlan:[
      {day:"Tuesday",focus:"Press resistance",session:"Playing through the press drill. GK involvement in build-up. Technical session."},
      {day:"Wednesday",focus:"Transition after breaking",session:"Transition 6v4 drill. Speed of attack after breaking the press."},
      {day:"Thursday",focus:"Long ball option",session:"Long ball target drill. Striker hold-up work. Second ball winning."},
      {day:"Friday",focus:"Match prep",session:"Walk through their press triggers. Practice the specific press-breaking patterns vs their shape."},
    ],
  },
  possession:{
    title:"Disrupting a Possession Team",
    threat:"A possession-dominant team will try to control the ball and the tempo. They want you to chase shadows and tire out. Your shape and patience are the weapons.",
    keyPrinciples:[
      "Don't chase the ball — hold your shape and force them into the zones you want",
      "Define your press triggers carefully — only press when the probability of winning is high",
      "Protect the space in behind your defensive line — their movement is designed to drag you out",
      "When you win the ball, go vertical immediately — they have players high and out of position",
      "Second balls and set pieces are opportunities — don't try to out-possess them",
    ],
    drills:[
      {title:"Organised Defensive Block",desc:"10v10 — possession team tries to break down a compact defensive block. Defending team works on holding the shape, covering passing lanes and pressing only at defined triggers.",duration:"20 min",players:"Full squad"},
      {title:"Press Trigger Definition",desc:"Define 3 press triggers vs their system. Practice the coordinated press from those specific triggers only. All other situations — hold the shape.",duration:"15 min",players:"Full squad"},
      {title:"Vertical Counter",desc:"When the ball is won, go vertical in 3 passes or fewer. Practice the direct forward pass, the runner in behind and the finish.",duration:"15 min",players:"6 attackers + GK"},
      {title:"Shape Recovery Sprint",desc:"Players sprint to recover defensive shape after losing the ball. Timed — must be in shape within 5 seconds. No possession team can exploit if you recover in time.",duration:"10 min",players:"Full squad"},
    ],
    playerFocusTasks:[
      "Defensive line: Never step out for the ball unless the trigger is met — hold the line",
      "Midfielders: Stay compact — don't be dragged out by their movement",
      "Wingers: Tuck inside to make it 4-4 or 4-5 out of possession",
      "Striker: Press only at the defined trigger — don't chase the ball all game",
      "Full-backs: Don't step up to press — cover the channel behind the winger",
    ],
    weekPlan:[
      {day:"Tuesday",focus:"Defensive shape",session:"Compact defensive block drill. Press trigger definition."},
      {day:"Wednesday",focus:"Counter-attack",session:"Vertical counter drill. Transition from block to attack in 3 passes."},
      {day:"Thursday",focus:"Set pieces",session:"Attacking set pieces — best chance to score vs a technical team."},
      {day:"Friday",focus:"Match prep",session:"Walk through their patterns. Define exactly when to press vs when to hold."},
    ],
  },
  physical:{
    title:"Handling Physical, Direct Opposition",
    threat:"A physical, direct team will try to bully you, win second balls and use set pieces to score. Technical quality and tactical discipline are your weapons.",
    keyPrinciples:[
      "Win the first ball — if you don't compete aerially, you will struggle all game",
      "Keep the ball on the floor — play short and quick, deny them the physical duels they want",
      "Second ball positioning is critical — know where the long ball will land and be there first",
      "Set pieces are their main goal threat — your defensive organisation must be perfect",
      "Physical battles in midfield are unavoidable — your CMs must be ready for it",
    ],
    drills:[
      {title:"Aerial Duel Training",desc:"Practice winning aerial duels — both attacking and defensive. Focus on the jump, the timing and the body position. Compete physically in every rep.",duration:"15 min",players:"Defenders + strikers"},
      {title:"Second Ball Winning",desc:"Long ball is played in. Both teams compete for the second ball. Whoever wins attacks immediately. Physical intensity is the priority.",duration:"20 min",players:"Full squad"},
      {title:"Keep Ball Under Physical Pressure",desc:"Possession game where the defending team is allowed to be physically aggressive. Forces technical players to cope with contact and still play.",duration:"15 min",players:"8v4"},
      {title:"Defensive Set Piece Rehearsal",desc:"Practice defending against their specific set piece threats. If they deliver near-post, practice clearing near-post. If they attack the back post, have a player there.",duration:"15 min",players:"Full squad"},
    ],
    playerFocusTasks:[
      "CBs: Must win every aerial duel — no excuses on set pieces",
      "CMs: Be ready for physical midfield battles — don't shy away from the duel",
      "Striker: Hold the ball and keep it on the floor — deny them the chaos they want",
      "Full-backs: Don't allow long throws into the box — position to cut them off",
      "GK: Command the box aggressively — claim everything you can reach",
    ],
    weekPlan:[
      {day:"Tuesday",focus:"Aerial work",session:"Aerial duel training. Second ball winning. Physical conditioning session."},
      {day:"Wednesday",focus:"Technical under pressure",session:"Keep ball under physical pressure. Short passing combinations at pace."},
      {day:"Thursday",focus:"Defensive set pieces",session:"Full defensive set piece session. Every corner, free kick and throw-in scenario."},
      {day:"Friday",focus:"Match prep",session:"Walk through their long ball targets. Define aerial responsibilities. Short 11v11."},
    ],
  },
  counter:{
    title:"Breaking Down a Counter-attacking Team",
    threat:"A counter-attacking team will sit back and invite pressure, then exploit the space behind your defensive line when you over-commit. Discipline is everything.",
    keyPrinciples:[
      "Attack patiently — never commit too many players forward at once",
      "Define a maximum number of attackers in the box — never leave yourself exposed",
      "Your full-backs must be disciplined — if they attack, a CB covers",
      "The moment you lose the ball, the defensive line must drop immediately",
      "Set pieces are the safest way to score — invest in your corner and free kick delivery",
    ],
    drills:[
      {title:"Controlled Attack Drill",desc:"Attack with a defined maximum of 4 players. The remaining 6 hold position. Practice attacking without over-committing and recovering if the ball is lost.",duration:"20 min",players:"Full squad"},
      {title:"Drop on Turnover",desc:"When possession is lost in the attacking third, the whole team drops immediately. Time the recovery — must be in shape before the opposition breaks the halfway line.",duration:"15 min",players:"Full squad"},
      {title:"Wide Crossing Attack",desc:"Create chances from wide areas rather than central overloads. Full-back overlaps and delivers — avoiding the space in behind that a direct striker could exploit.",duration:"15 min",players:"Full-backs + wingers + forwards"},
      {title:"Defensive Transition Sprint",desc:"Attack — lose the ball — sprint back. Defender chases striker. CB covers. Full-back tucks. Repeat 8 times at match intensity.",duration:"12 min",players:"Full squad"},
    ],
    playerFocusTasks:[
      "Full-backs: If you attack, a CB must cover — never leave 2v1 in behind",
      "CMs: One CM stays — never both attack at the same time",
      "Wingers: Deliver from wide rather than cutting inside into the space they want to exploit",
      "Striker: Draw defenders out — create space for the CMs to arrive without over-committing",
      "Defensive line: Drop the moment possession is lost — no hesitation",
    ],
    weekPlan:[
      {day:"Tuesday",focus:"Controlled attack",session:"Controlled attack drill. Define the rules — max 4 attackers, others hold."},
      {day:"Wednesday",focus:"Defensive transition",session:"Drop on turnover drill. Sprint recovery work. Transition shape."},
      {day:"Thursday",focus:"Wide attack patterns",session:"Wide crossing drill. Set piece rehearsal."},
      {day:"Friday",focus:"Match prep",session:"Walk through their counter-attack patterns. Identify their pace threat and assign a defender to cover."},
    ],
  },
  pressing:{
    title:"Playing Against an Active Mid-block Press",
    threat:"A mid-block pressing team will press in waves — not continuously, but aggressively when triggered. They want to disrupt your rhythm and force turnovers in transition zones.",
    keyPrinciples:[
      "Recognise the press trigger — their press is triggered, not continuous. Find the pattern.",
      "Play quickly through the zones where they press — don't sit in the press zone",
      "The third man combination is the key — use the player beyond the press",
      "Width is your friend — get the ball wide and the press loses its shape",
      "If in doubt, go back — reset and try again. Don't force passes into pressure.",
    ],
    drills:[
      {title:"Third Man Combination",desc:"Pass — press activates — play to the third man who is beyond the press. The third man combination breaks any press. Practice the timing and the movement.",duration:"18 min",players:"Groups of 4"},
      {title:"Pattern Recognition Drill",desc:"Show footage of the opposition's press triggers. Players must identify the trigger during a possession game and react before the press arrives.",duration:"15 min",players:"Full squad"},
      {title:"Wide Release Drill",desc:"When pressed centrally, release wide. Full-back and winger combine — the press can't cover both. Practice the switch to the free wide player.",duration:"15 min",players:"Back line + midfield + wingers"},
      {title:"Reset Discipline",desc:"Practice going back — GK back pass, CB to GK, CDM to CB. Build the culture of resetting rather than forcing it. Frustrate the press by making them run.",duration:"10 min",players:"Back 6 + GK"},
    ],
    playerFocusTasks:[
      "GK: Must be a constant reset option — comfortable receiving back passes",
      "CDM: Position to receive between their press lines — be the third man constantly",
      "Full-backs: Stay wide — be the release valve when the centre is pressed",
      "Wingers: When the full-back has the ball, make a run — drag their midfielder out of position",
      "Striker: Hold the ball as the third man after the combination breaks the press",
    ],
    weekPlan:[
      {day:"Tuesday",focus:"Third man combinations",session:"Third man combination drill. Wide release patterns."},
      {day:"Wednesday",focus:"Press trigger recognition",session:"Pattern recognition drill using video. Possess under the press."},
      {day:"Thursday",focus:"Reset discipline",session:"Reset drill. Practice the back pass culture. Technical possession."},
      {day:"Friday",focus:"Match prep",session:"Walk through their press shape. Practice 3 specific patterns to beat their press."},
    ],
  },
};

/* ─────────────────────────
   HELPER: get drills for style
───────────────────────── */
function getDrillsForStyle(style){
  const direct = INDIVIDUAL_DRILLS[style];
  if(direct) return direct;
  // Find closest match
  const keys = Object.keys(INDIVIDUAL_DRILLS);
  for(let k of keys){
    if(style.toLowerCase().includes(k.toLowerCase().split(" ")[0])) return INDIVIDUAL_DRILLS[k];
  }
  return DEFAULT_DRILLS;
}

/* ─────────────────────────
   PRO PLAYER ATTRS (approximate)
───────────────────────── */
function getProAttrs(proName){
  const proMap = {
    "Erling Haaland":{pace:89,shooting:94,passing:65,dribbling:80,defending:35,physical:88},
    "Harry Kane":{pace:70,shooting:91,passing:83,dribbling:78,defending:42,physical:83},
    "Mohamed Salah":{pace:91,shooting:89,passing:80,dribbling:90,defending:46,physical:76},
    "Bukayo Saka":{pace:83,shooting:80,passing:82,dribbling:87,defending:59,physical:72},
    "Kevin De Bruyne":{pace:76,shooting:82,passing:95,dribbling:83,defending:64,physical:77},
    "Martin Ødegaard":{pace:74,shooting:81,passing:92,dribbling:87,defending:60,physical:65},
    "Rodri":{pace:68,shooting:72,passing:89,dribbling:74,defending:89,physical:80},
    "Declan Rice":{pace:72,shooting:75,passing:84,dribbling:78,defending:84,physical:85},
    "Virgil van Dijk":{pace:78,shooting:60,passing:80,dribbling:72,defending:92,physical:90},
    "Trent Alexander-Arnold":{pace:79,shooting:75,passing:93,dribbling:76,defending:67,physical:66},
    "Alisson Becker":{pace:52,shooting:25,passing:78,dribbling:44,defending:90,physical:80},
    "Ederson":{pace:55,shooting:30,passing:82,dribbling:48,defending:88,physical:78},
    "Jude Bellingham":{pace:80,shooting:83,passing:85,dribbling:87,defending:72,physical:83},
    "Vinícius Jr.":{pace:94,shooting:84,passing:75,dribbling:93,defending:38,physical:73},
  };
  return proMap[proName]||{pace:75,shooting:75,passing:75,dribbling:75,defending:60,physical:75};
}

function attrC(v){return v>=80?"av-hi":v>=65?"av-md":"av-lo";}
function ini(n){return n&&n.split(" ").map(w=>w[0]).join("")||"?";}
function posGroup(pos){
  if(pos==="GK") return "gk";
  if(["CB","RB","LB","WB","RWB","LWB"].includes(pos)) return "def";
  if(["CDM","CM","CAM","RM","LM"].includes(pos)) return "mid";
  return "att";
}

/* ─────────────────────────
   FORMATION SLOT POSITIONS
───────────────────────── */
const FORMATION_SLOTS = {
  "4-3-3":[
    {pos:"GK",x:50,y:90},{pos:"RB",x:80,y:72},{pos:"CB",x:62,y:75},{pos:"CB",x:38,y:75},{pos:"LB",x:20,y:72},
    {pos:"CM",x:70,y:52},{pos:"CM",x:50,y:48},{pos:"CM",x:30,y:52},
    {pos:"RW",x:80,y:28},{pos:"ST",x:50,y:20},{pos:"LW",x:20,y:28},
  ],
  "4-4-2":[
    {pos:"GK",x:50,y:90},{pos:"RB",x:80,y:72},{pos:"CB",x:62,y:75},{pos:"CB",x:38,y:75},{pos:"LB",x:20,y:72},
    {pos:"RM",x:82,y:50},{pos:"CM",x:62,y:50},{pos:"CM",x:38,y:50},{pos:"LM",x:18,y:50},
    {pos:"ST",x:62,y:22},{pos:"ST",x:38,y:22},
  ],
  "4-2-3-1":[
    {pos:"GK",x:50,y:90},{pos:"RB",x:80,y:72},{pos:"CB",x:62,y:75},{pos:"CB",x:38,y:75},{pos:"LB",x:20,y:72},
    {pos:"CDM",x:62,y:55},{pos:"CDM",x:38,y:55},
    {pos:"RW",x:80,y:35},{pos:"CAM",x:50,y:32},{pos:"LW",x:20,y:35},
    {pos:"ST",x:50,y:18},
  ],
  "3-4-2-1":[
    {pos:"GK",x:50,y:90},{pos:"CB",x:28,y:75},{pos:"CB",x:50,y:78},{pos:"CB",x:72,y:75},
    {pos:"RWB",x:85,y:52},{pos:"CM",x:62,y:52},{pos:"CM",x:38,y:52},{pos:"LWB",x:15,y:52},
    {pos:"CAM",x:65,y:30},{pos:"CAM",x:35,y:30},
    {pos:"ST",x:50,y:18},
  ],
  "4-3-3 (Arteta)":[
    {pos:"GK",x:50,y:90},{pos:"RB",x:80,y:72},{pos:"CB",x:62,y:75},{pos:"CB",x:38,y:75},{pos:"LB",x:20,y:72},
    {pos:"CDM",x:50,y:55},{pos:"CM",x:70,y:45},{pos:"CM",x:30,y:45},
    {pos:"RW",x:82,y:26},{pos:"ST",x:50,y:18},{pos:"LW",x:18,y:26},
  ],
};

/* ══════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════ */
/* ══════════════════════════════════════
   INTERACTIVE PITCH COMPONENT
   Canvas-based · Fully draggable · Drop players from list
══════════════════════════════════════ */
function InteractivePitch({pitchTokens,setPitchTokens,selectedPitchToken,setSelectedPitchToken,onEmptySlotClick,onTokenClick}){
  const canvasRef = useRef(null);
  const wrapRef   = useRef(null);
  const [size, setSize]           = useState({w:600,h:420});
  const [dragOverTok, setDragOverTok] = useState(null);
  const draggingRef = useRef(false);

  // Resize observer
  useEffect(()=>{
    const el = wrapRef.current;
    if(!el) return;
    const obs = new ResizeObserver(e=>{
      const r = e[0].contentRect;
      setSize({w:Math.floor(r.width), h:Math.floor(r.height)});
    });
    obs.observe(el);
    return ()=>obs.disconnect();
  },[]);

  const TOKEN_R = Math.min(size.w, size.h) * 0.052;
  const px = useCallback((xp,yp)=>({x:(xp/100)*size.w, y:(yp/100)*size.h}),[size]);

  // ── DRAW ──
  useEffect(()=>{
    const cv = canvasRef.current;
    if(!cv) return;
    const ctx = cv.getContext("2d");
    const {w,h} = size;
    cv.width=w; cv.height=h;

    // Background
    const g = ctx.createLinearGradient(0,0,0,h);
    g.addColorStop(0,"#3a6b42"); g.addColorStop(0.3,"#4a7c52");
    g.addColorStop(0.5,"#3d6e45"); g.addColorStop(0.7,"#4a7c52"); g.addColorStop(1,"#3a6b42");
    ctx.fillStyle=g; ctx.fillRect(0,0,w,h);
    for(let i=0;i<10;i++){if(i%2===0){ctx.fillStyle="rgba(0,0,0,0.03)";ctx.fillRect(i*(w/10),0,w/10,h);}}

    // Markings
    ctx.strokeStyle="rgba(255,255,255,0.4)"; ctx.lineWidth=1.2;
    ctx.beginPath(); ctx.rect(w*.05,h*.03,w*.9,h*.94); ctx.stroke();
    ctx.beginPath(); ctx.moveTo(w*.05,h*.5); ctx.lineTo(w*.95,h*.5); ctx.stroke();
    ctx.beginPath(); ctx.arc(w*.5,h*.5,w*.12,0,Math.PI*2); ctx.stroke();
    ctx.beginPath(); ctx.rect(w*.22,h*.03,w*.56,h*.18); ctx.stroke();
    ctx.beginPath(); ctx.rect(w*.22,h*.79,w*.56,h*.18); ctx.stroke();
    ctx.beginPath(); ctx.rect(w*.34,h*.03,w*.32,h*.08); ctx.stroke();
    ctx.beginPath(); ctx.rect(w*.34,h*.89,w*.32,h*.08); ctx.stroke();
    ctx.fillStyle="rgba(255,255,255,0.5)";
    [[w*.5,h*.5],[w*.5,h*.14],[w*.5,h*.86]].forEach(([cx,cy])=>{ctx.beginPath();ctx.arc(cx,cy,3,0,Math.PI*2);ctx.fill();});
    ctx.strokeStyle="rgba(255,255,255,0.6)"; ctx.lineWidth=2;
    ctx.beginPath(); ctx.rect(w*.41,0,w*.18,h*.03); ctx.stroke();
    ctx.beginPath(); ctx.rect(w*.41,h*.97,w*.18,h*.03); ctx.stroke();

    // Tokens
    pitchTokens.forEach(tok=>{
      const p = px(tok.x,tok.y);
      const isSel = selectedPitchToken===tok.id;
      const isHover = dragOverTok===tok.id;
      const hasPlayer = !!tok.player;
      const grp = tok.player?.group||posGroup(tok.pos);
      const colors={gk:"#f59e0b",def:"#8b2a2a",mid:"#2e7d7a",att:"#16a34a"};

      // Glow
      if(isSel||isHover){
        ctx.strokeStyle=isHover?"rgba(255,255,255,0.9)":"rgba(212,148,26,0.6)";
        ctx.lineWidth=isHover?5:7;
        ctx.beginPath(); ctx.arc(p.x,p.y,TOKEN_R+5,0,Math.PI*2); ctx.stroke();
      }

      // Fill
      ctx.fillStyle=hasPlayer?(colors[grp]||"#2e7d7a"):"rgba(255,255,255,0.12)";
      ctx.beginPath(); ctx.arc(p.x,p.y,TOKEN_R,0,Math.PI*2); ctx.fill();

      // Border
      ctx.strokeStyle=hasPlayer?(isSel?"#FFD700":"rgba(255,255,255,0.8)"):(isSel?"#FFD700":"rgba(255,255,255,0.3)");
      ctx.lineWidth=isSel?3:1.8;
      if(!hasPlayer) ctx.setLineDash([4,3]);
      ctx.beginPath(); ctx.arc(p.x,p.y,TOKEN_R,0,Math.PI*2); ctx.stroke();
      ctx.setLineDash([]);

      // Text
      ctx.textAlign="center"; ctx.textBaseline="middle";
      if(hasPlayer){
        const pl=tok.player;
        const initials=pl.name.split(" ").map(w=>w[0]).join("").slice(0,2).toUpperCase();
        ctx.fillStyle="#fff"; ctx.font=`bold ${TOKEN_R*.72}px DM Sans`;
        ctx.fillText(initials,p.x,p.y-2);
        ctx.fillStyle="#FFD700"; ctx.font=`bold ${TOKEN_R*.52}px DM Sans`;
        ctx.fillText(pl.overall||70,p.x,p.y+TOKEN_R*.52);
        // Name label
        const first=pl.name.split(" ")[0];
        const lw=Math.min(first.length*6+10,78);
        ctx.fillStyle="rgba(0,0,0,0.72)";
        ctx.beginPath(); ctx.roundRect(p.x-lw/2,p.y+TOKEN_R+3,lw,16,3); ctx.fill();
        ctx.fillStyle="#fff"; ctx.font=`bold ${TOKEN_R*.48}px DM Sans`;
        ctx.fillText(first,p.x,p.y+TOKEN_R+11);
      } else {
        ctx.fillStyle="rgba(255,255,255,0.65)"; ctx.font=`bold ${TOKEN_R*.58}px DM Sans`;
        ctx.fillText(tok.pos,p.x,p.y-2);
        ctx.fillStyle="rgba(255,255,255,0.3)"; ctx.font=`${TOKEN_R*.68}px DM Sans`;
        ctx.fillText("+",p.x,p.y+TOKEN_R*.62);
      }
    });
  },[pitchTokens,selectedPitchToken,dragOverTok,size,TOKEN_R,px]);

  // ── COORD HELPERS ──
  const toP = useCallback((cx,cy)=>{
    const cv=canvasRef.current; if(!cv) return{x:50,y:50};
    const r=cv.getBoundingClientRect();
    return{x:((cx-r.left)/r.width)*100, y:((cy-r.top)/r.height)*100};
  },[]);

  const hitTok = useCallback((xp,yp)=>{
    const {w,h}=size;
    return pitchTokens.find(t=>{
      const dx=((t.x-xp)/100)*w, dy=((t.y-yp)/100)*h;
      return Math.sqrt(dx*dx+dy*dy)<TOKEN_R+8;
    });
  },[pitchTokens,size,TOKEN_R]);

  // ── MOUSE DRAG (reposition token) ──
  const onMouseDown = useCallback(e=>{
    const {x,y}=toP(e.clientX,e.clientY);
    const hit=hitTok(x,y);
    if(!hit) return;
    setSelectedPitchToken(hit.id);
    draggingRef.current=false;
    const sx=x,sy=y;
    const onM=me=>{
      const {x:nx,y:ny}=toP(me.clientX,me.clientY);
      if(Math.abs(nx-sx)>1||Math.abs(ny-sy)>1) draggingRef.current=true;
      if(draggingRef.current){
        setPitchTokens(prev=>prev.map(t=>t.id===hit.id?{...t,x:Math.max(3,Math.min(97,nx)),y:Math.max(3,Math.min(97,ny))}:t));
      }
    };
    const onU=()=>{
      setTimeout(()=>{draggingRef.current=false;},50);
      document.removeEventListener("mousemove",onM);
      document.removeEventListener("mouseup",onU);
    };
    document.addEventListener("mousemove",onM);
    document.addEventListener("mouseup",onU);
  },[toP,hitTok,setSelectedPitchToken,setPitchTokens]);

  // ── CLICK ──
  const onCanvasClick = useCallback(e=>{
    if(draggingRef.current) return;
    const {x,y}=toP(e.clientX,e.clientY);
    const hit=hitTok(x,y);
    if(hit){
      setSelectedPitchToken(hit.id);
      if(hit.player&&onTokenClick) onTokenClick(hit);
      if(!hit.player&&onEmptySlotClick) onEmptySlotClick(hit);
    } else {
      setSelectedPitchToken(null);
    }
  },[toP,hitTok,setSelectedPitchToken,onTokenClick,onEmptySlotClick]);

  // ── HTML5 DRAG (squad list → pitch, or token → token swap) ──
  const onDragStartCanvas = useCallback(e=>{
    const {x,y}=toP(e.clientX,e.clientY);
    const hit=hitTok(x,y);
    if(hit?.player){
      e.dataTransfer.setData("pitch_token_id",hit.id);
      e.dataTransfer.effectAllowed="move";
    } else {
      e.preventDefault();
    }
  },[toP,hitTok]);

  const onDragOver = useCallback(e=>{
    e.preventDefault();
    const {x,y}=toP(e.clientX,e.clientY);
    const hit=hitTok(x,y);
    setDragOverTok(hit?.id||null);
  },[toP,hitTok]);

  const onDrop = useCallback(e=>{
    e.preventDefault();
    setDragOverTok(null);
    const {x,y}=toP(e.clientX,e.clientY);
    const targetTok=hitTok(x,y);

    // From squad list
    const playerData=e.dataTransfer.getData("squad_player");
    if(playerData){
      const player=JSON.parse(playerData);
      if(targetTok){
        setPitchTokens(prev=>prev.map(t=>t.id===targetTok.id?{...t,player}:t));
      } else {
        const nearest=pitchTokens
          .filter(t=>!t.player)
          .sort((a,b)=>(Math.pow(a.x-x,2)+Math.pow(a.y-y,2))-(Math.pow(b.x-x,2)+Math.pow(b.y-y,2)))[0];
        if(nearest) setPitchTokens(prev=>prev.map(t=>t.id===nearest.id?{...t,player}:t));
      }
      return;
    }

    // Pitch token → pitch token swap
    const fromId=e.dataTransfer.getData("pitch_token_id");
    if(fromId&&targetTok&&targetTok.id!==fromId){
      setPitchTokens(prev=>{
        const fromTok=prev.find(t=>t.id===fromId);
        if(!fromTok) return prev;
        return prev.map(t=>{
          if(t.id===fromId) return{...t,player:targetTok.player};
          if(t.id===targetTok.id) return{...t,player:fromTok.player};
          return t;
        });
      });
    }
  },[toP,hitTok,pitchTokens,setPitchTokens]);

  return(
    <div ref={wrapRef}
      style={{position:"relative",height:"min(420px,56vw)",background:"#3a6b42",overflow:"hidden"}}
      onDragOver={onDragOver}
      onDragLeave={()=>setDragOverTok(null)}
      onDrop={onDrop}>
      <canvas
        ref={canvasRef}
        style={{position:"absolute",inset:0,cursor:"pointer",touchAction:"none",display:"block"}}
        draggable
        onDragStart={onDragStartCanvas}
        onMouseDown={onMouseDown}
        onClick={onCanvasClick}
      />
      {pitchTokens.filter(t=>t.player).length===0&&(
        <div style={{position:"absolute",top:"50%",left:"50%",transform:"translate(-50%,-50%)",textAlign:"center",pointerEvents:"none",padding:"1rem"}}>
          <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",color:"rgba(255,255,255,0.5)",fontStyle:"italic",fontWeight:300,lineHeight:2}}>
            Drag players from the list onto the pitch<br/>
            Click a position slot to add a player<br/>
            <span style={{fontSize:"0.65rem",opacity:0.7}}>Drag tokens between slots to swap positions</span>
          </div>
        </div>
      )}
    </div>
  );
}


/* ═══════════════════════════════════════════════════════════
   PLAYER PROFILES TAB
   Shows full career record for each player:
   appearances, goals, assists, avg rating, form, notes
═══════════════════════════════════════════════════════════ */
function PlayerProfilesTab({ squad, onNavigate }) {
  const [selId, setSelId] = useState(null);
  const [notes, setNotes] = useState({});
  const [editingNote, setEditingNote] = useState(null);

  // Load all match rating records
  const allRatings = useMemo(() => loadFromStorage("player_match_ratings", []) || [], []);

  // Compute per-player stats from rating records
  const playerStats = useMemo(() => {
    const stats = {};
    squad.forEach(p => {
      stats[p.id] = {
        appearances: 0,
        goals:       p.goals || 0,
        assists:     p.assists || 0,
        ratings:     [],
        matches:     [],
      };
    });
    allRatings.forEach(match => {
      Object.entries(match.ratings || {}).forEach(([pid, rating]) => {
        if (stats[pid] && rating > 0) {
          stats[pid].appearances++;
          stats[pid].ratings.push(rating);
          stats[pid].matches.push({
            opponent:  match.opponent,
            date:      match.date,
            result:    match.result,
            score:     match.score,
            rating,
          });
        }
      });
    });
    // Sort matches newest first
    Object.values(stats).forEach(s => s.matches.sort((a,b) => new Date(b.date)-new Date(a.date)));
    return stats;
  }, [squad, allRatings]);

  const avgRating = (ratings) => ratings.length > 0 ? (ratings.reduce((s,r)=>s+r,0)/ratings.length).toFixed(1) : "—";
  const form = (matches) => matches.slice(0,5).map(m => m.result==="Win"?"W":m.result==="Loss"?"L":"D");
  const ratingColor = (r) => r>=8?"#16a34a":r>=7?"var(--teal)":r>=6?"var(--mustard)":r>=5?"#f59e0b":"var(--darkred)";

  const selectedPlayer = squad.find(p=>String(p.id)===String(selId));
  const selectedStats  = selId ? playerStats[selId] : null;

  return (
    <div style={{padding:"1.2rem 1.5rem"}}>
      {selId && selectedPlayer ? (
        /* ── PLAYER DETAIL VIEW ── */
        <>
          <button onClick={()=>setSelId(null)} style={{background:"transparent",border:"none",fontFamily:"'DM Sans',sans-serif",fontSize:"0.7rem",fontWeight:700,color:"var(--muted)",cursor:"pointer",padding:0,marginBottom:"1rem",display:"flex",alignItems:"center",gap:"0.35rem"}}>
            ← All Players
          </button>

          {/* Header */}
          <div style={{background:"var(--ink)",padding:"1.2rem 1.5rem",borderBottom:"3px solid var(--mustard)",marginBottom:"1.2rem",display:"flex",gap:"1.5rem",alignItems:"flex-start",flexWrap:"wrap"}}>
            <div style={{width:52,height:52,borderRadius:"50%",background:"var(--teal)",border:"2px solid rgba(255,255,255,0.2)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.2rem",color:"white",flexShrink:0}}>
              {selectedPlayer.name.split(" ").map(w=>w[0]).join("").slice(0,2)}
            </div>
            <div style={{flex:1}}>
              <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.6rem",letterSpacing:"3px",color:"var(--white)",lineHeight:1}}>
                {selectedPlayer.name}
              </div>
              <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",color:"rgba(255,255,255,0.5)",marginTop:"0.2rem"}}>
                {selectedPlayer.pos} · {selectedPlayer.style||"—"} · {selectedPlayer.age||"—"} yrs · {selectedPlayer.nationality||"—"}
              </div>
              {selectedPlayer.club && <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",color:"rgba(255,255,255,0.35)",marginTop:"0.1rem"}}>{selectedPlayer.club}</div>}
            </div>
            {/* Season stats */}
            <div style={{display:"flex",gap:"1.2rem"}}>
              {[
                {l:"Apps",   v:selectedStats?.appearances||0},
                {l:"Goals",  v:selectedStats?.goals||0},
                {l:"Assists",v:selectedStats?.assists||0},
                {l:"Avg Rat",v:avgRating(selectedStats?.ratings||[])},
              ].map((s,i)=>(
                <div key={i} style={{textAlign:"center"}}>
                  <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.6rem",letterSpacing:"2px",color:"var(--mustard)",lineHeight:1}}>{s.v}</div>
                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.52rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"rgba(255,255,255,0.35)",marginTop:"0.1rem"}}>{s.l}</div>
                </div>
              ))}
            </div>
          </div>

          <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"1rem"}}>
            {/* Attributes */}
            <div style={{background:"var(--white)",border:"1.5px solid var(--rule)"}}>
              <div style={{padding:"0.6rem 0.85rem",borderBottom:"1.5px solid var(--rule)",fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.78rem",letterSpacing:"2px",color:"var(--ink)"}}>Attributes</div>
              <div style={{padding:"0.75rem 0.85rem"}}>
                {[["PAC",selectedPlayer.pace],["SHO",selectedPlayer.shooting],["PAS",selectedPlayer.passing],["DRI",selectedPlayer.dribbling],["DEF",selectedPlayer.defending],["PHY",selectedPlayer.physical]].map(([k,v])=>(
                  <div key={k} style={{display:"flex",alignItems:"center",gap:"0.6rem",padding:"0.32rem 0",borderBottom:"1px solid var(--rule2)"}}>
                    <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",fontWeight:800,letterSpacing:"2px",color:"var(--muted)",width:28}}>{k}</span>
                    <div style={{flex:1,height:5,background:"var(--rule)",borderRadius:3,overflow:"hidden"}}>
                      <div style={{height:"100%",width:`${v}%`,background:v>=80?"var(--teal)":v>=65?"var(--mustard)":"var(--darkred)",borderRadius:3}}/>
                    </div>
                    <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.95rem",letterSpacing:"1px",color:v>=80?"var(--teal)":v>=65?"var(--mustard)":"var(--darkred)",width:24,textAlign:"right"}}>{v}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Form + Recent matches */}
            <div style={{background:"var(--white)",border:"1.5px solid var(--rule)"}}>
              <div style={{padding:"0.6rem 0.85rem",borderBottom:"1.5px solid var(--rule)",fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.78rem",letterSpacing:"2px",color:"var(--ink)"}}>Recent Form</div>
              <div style={{padding:"0.75rem 0.85rem"}}>
                {/* Form badges */}
                <div style={{display:"flex",gap:"0.3rem",marginBottom:"0.85rem"}}>
                  {form(selectedStats?.matches||[]).map((r,i)=>(
                    <div key={i} style={{width:28,height:28,borderRadius:3,background:r==="W"?"#16a34a":r==="L"?"var(--darkred)":"var(--mustard)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.82rem",letterSpacing:"1px",color:"white"}}>{r}</div>
                  ))}
                  {(selectedStats?.matches||[]).length===0&&<div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.7rem",color:"var(--muted)",fontStyle:"italic"}}>No matches rated yet</div>}
                </div>
                {/* Match list */}
                {(selectedStats?.matches||[]).slice(0,6).map((m,i)=>(
                  <div key={i} style={{display:"flex",alignItems:"center",gap:"0.5rem",padding:"0.3rem 0",borderBottom:"1px solid var(--rule2)"}}>
                    <div style={{width:20,height:20,borderRadius:2,background:m.result==="Win"?"#16a34a":m.result==="Loss"?"var(--darkred)":"var(--mustard)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.65rem",color:"white",flexShrink:0}}>
                      {m.result==="Win"?"W":m.result==="Loss"?"L":"D"}
                    </div>
                    <div style={{flex:1,fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",color:"var(--ink)",fontWeight:600,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{m.opponent}</div>
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",color:"var(--muted)"}}>{m.score}</div>
                    <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.85rem",letterSpacing:"1px",color:ratingColor(m.rating),width:18,textAlign:"right",flexShrink:0}}>{m.rating}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Coach notes */}
          <div style={{background:"var(--white)",border:"1.5px solid var(--rule)",marginTop:"1rem"}}>
            <div style={{padding:"0.6rem 0.85rem",borderBottom:"1.5px solid var(--rule)",display:"flex",justifyContent:"space-between",alignItems:"center"}}>
              <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.78rem",letterSpacing:"2px",color:"var(--ink)"}}>Coach Notes</div>
              <button onClick={()=>setEditingNote(editingNote===selId?null:selId)} style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",fontWeight:700,background:"transparent",border:"none",color:"var(--teal)",cursor:"pointer"}}>
                {editingNote===selId?"Save Notes":"Edit"}
              </button>
            </div>
            <div style={{padding:"0.85rem"}}>
              {editingNote===selId ? (
                <textarea
                  value={notes[selId]||""}
                  onChange={e=>setNotes(prev=>({...prev,[selId]:e.target.value}))}
                  onBlur={()=>{
                    const allNotes = loadFromStorage("player_coach_notes",{});
                    saveToStorage("player_coach_notes",{...allNotes,[selId]:notes[selId]||""});
                    setEditingNote(null);
                  }}
                  autoFocus
                  style={{width:"100%",minHeight:100,background:"var(--cream)",border:"1.5px solid var(--rule)",borderBottom:"2px solid var(--teal)",padding:"0.6rem 0.8rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",fontStyle:"italic",outline:"none",resize:"vertical",color:"var(--ink)"}}
                  placeholder="Private coaching notes — development focus, personality, tactical instructions, contract situation..."
                />
              ) : (
                <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",color:notes[selId]?"var(--ink2)":"var(--muted)",fontStyle:"italic",lineHeight:1.7,fontWeight:300}}>
                  {notes[selId]||"No notes yet. Click Edit to add private coaching notes for this player."}
                </div>
              )}
            </div>
          </div>
        </>
      ) : (
        /* ── SQUAD LIST VIEW ── */
        <>
          <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",color:"var(--muted)",fontStyle:"italic",marginBottom:"1.2rem",lineHeight:1.65}}>
            Full season record for every player. Rate players after each match in Fixtures → Log Result to build their profiles automatically.
          </div>

          {squad.length === 0 ? (
            <div style={{textAlign:"center",padding:"3rem",color:"var(--muted)",fontFamily:"'DM Sans',sans-serif",fontSize:"0.82rem",fontStyle:"italic"}}>
              No squad members yet. Add players in the My Squad tab first.
            </div>
          ) : (
            <div style={{display:"flex",flexDirection:"column",gap:"0.4rem"}}>
              {["gk","def","mid","att"].map(grp => {
                const grpPlayers = squad.filter(p=>(p.group||"att")===grp);
                if(!grpPlayers.length) return null;
                const grpLabel = {gk:"Goalkeepers",def:"Defenders",mid:"Midfielders",att:"Attackers"}[grp];
                return (
                  <div key={grp}>
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",fontWeight:800,letterSpacing:"2.5px",textTransform:"uppercase",color:"var(--muted)",padding:"0.5rem 0 0.3rem"}}>{grpLabel}</div>
                    {grpPlayers.map(p => {
                      const st = playerStats[p.id] || {appearances:0,ratings:[],matches:[]};
                      const avg = avgRating(st.ratings);
                      const recentForm = form(st.matches);
                      return (
                        <div key={p.id}
                          onClick={()=>{setSelId(p.id);const saved=loadFromStorage("player_coach_notes",{});setNotes(saved);}}
                          style={{background:"var(--white)",border:"1.5px solid var(--rule)",padding:"0.75rem 1rem",cursor:"pointer",display:"flex",alignItems:"center",gap:"0.85rem",transition:"all 0.15s"}}
                          onMouseEnter={e=>{e.currentTarget.style.borderColor="var(--teal)";e.currentTarget.style.boxShadow="2px 2px 0 var(--teal)";}}
                          onMouseLeave={e=>{e.currentTarget.style.borderColor="var(--rule)";e.currentTarget.style.boxShadow="";}}>
                          {/* Avatar */}
                          <div style={{width:36,height:36,borderRadius:"50%",background:"var(--teal4)",border:"1.5px solid var(--teal)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.8rem",color:"var(--teal)",flexShrink:0}}>
                            {p.name.split(" ").map(w=>w[0]).join("").slice(0,2)}
                          </div>
                          {/* Name + position */}
                          <div style={{minWidth:160,flexShrink:0}}>
                            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.82rem",fontWeight:800,color:"var(--ink)"}}>{p.name}</div>
                            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--muted)"}}>{p.pos} · {p.age||"—"} yrs · OVR {p.overall||"—"}</div>
                          </div>
                          {/* Season stats */}
                          <div style={{display:"flex",gap:"1rem",flexShrink:0}}>
                            {[["Apps",st.appearances],["Rating",avg]].map(([l,v])=>(
                              <div key={l} style={{textAlign:"center"}}>
                                <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.1rem",letterSpacing:"1px",color:l==="Rating"&&v!=="—"?ratingColor(Number(v)):"var(--ink)",lineHeight:1}}>{v}</div>
                                <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.5rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--muted)"}}>{l}</div>
                              </div>
                            ))}
                          </div>
                          {/* Form */}
                          <div style={{display:"flex",gap:"0.2rem",flex:1}}>
                            {recentForm.length===0
                              ? <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--muted)",fontStyle:"italic"}}>No matches yet</div>
                              : recentForm.map((r,i)=>(
                                <div key={i} style={{width:20,height:20,borderRadius:2,background:r==="W"?"#16a34a":r==="L"?"var(--darkred)":"var(--mustard)",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.6rem",color:"white"}}>{r}</div>
                              ))
                            }
                          </div>
                          <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--teal)",fontWeight:700,flexShrink:0}}>View →</span>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default function SquadCoach({externalPlayers=[], sharedSquad=null, onSquadChange=null, onNavigate=null}){
  const [activeTab,setActiveTab]=useState("squad");
  const toast = useToast();
  const [formation,setFormation]=useState("4-3-3");

  // Use shared squad from parent if provided, otherwise load from storage
  const [squad,setSquadLocal]=useState(()=> sharedSquad || loadFromStorage('my_squad',[]));

  // Keep in sync if parent passes updated sharedSquad
  useEffect(()=>{
    if(sharedSquad) setSquadLocal(sharedSquad);
  },[sharedSquad]);

  // Wrapper that updates both local state and notifies parent
  const setSquad = (updater) => {
    setSquadLocal(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      saveToStorage('my_squad', next);
      if(onSquadChange) onSquadChange(next);
      return next;
    });
  };
  const [showAddModal,setShowAddModal]=useState(false);
  const [addTab,setAddTab]=useState("new");
  const [dbSearch,setDbSearch]=useState("");
  const [selectedPlayer,setSelectedPlayer]=useState(null);
  const [devPlayer,setDevPlayer]=useState(null);

  // Pitch token state — tracks where players are positioned on the pitch
  const [pitchTokens,setPitchTokens]=useState([]);
  const [selectedPitchToken,setSelectedPitchToken]=useState(null);

  // Sync formation change to pitch tokens
  const handleFormationChange = (newFormation) => {
    setFormation(newFormation);
    // Reset pitch tokens to default slots for new formation, keeping existing player assignments
    const newSlots = FORMATION_SLOTS[newFormation]||FORMATION_SLOTS["4-3-3"];
    setPitchTokens(prev => newSlots.map((slot,i) => {
      const existing = prev[i];
      return { id:`slot-${i}`, x:slot.x, y:slot.y, pos:slot.pos, num:slot.num, player: existing?.player||null };
    }));
  };

  // Init pitch tokens when formation first loads
  useEffect(()=>{
    const slots = FORMATION_SLOTS[formation]||FORMATION_SLOTS["4-3-3"];
    setPitchTokens(slots.map((slot,i)=>({id:`slot-${i}`,x:slot.x,y:slot.y,pos:slot.pos,num:slot.num,player:null})));
  },[]);

  // New player form state
  const [newPlayer,setNewPlayer]=useState({
    name:"", pos:"ST", style:"Clinical Finisher", proRef:"",
    age:22, nationality:"English", club:"", potential:75,
    pace:75, shooting:75, passing:75, dribbling:75, defending:50, physical:75,
  });
  const [editingPlayer, setEditingPlayer] = useState(null); // player being edited inline

  // Training planner state
  const [planSystem,setPlanSystem]=useState("city433");
  const [planPhase,setPlanPhase]=useState("matchweek");
  const [planGenerated,setPlanGenerated]=useState(false);

  // Session drills — collected from Knowledge Base
  const [sessionDrills, setSessionDrills] = useState(() => getSessionDrills());
  const refreshSessionDrills = () => setSessionDrills(getSessionDrills());

  // Opposition prep state
  const [oppType,setOppType]=useState(null);
  const [oppPlanGenerated,setOppPlanGenerated]=useState(false);

  const slots = FORMATION_SLOTS[formation]||FORMATION_SLOTS["4-3-3"];

  // Combine external DB players with typed players for search
  // "My Squad" search — lets coach re-add players from their own saved squad list
  const allAvailablePlayers = useMemo(()=>{
    // Use the sharedSquad players NOT already in squad, so coach can add back removed players
    // Also include players from localStorage directly in case sharedSquad isn't current
    const saved = loadFromStorage('my_squad',[]) || [];
    const inSquad = new Set(squad.map(p=>String(p.id)));
    return saved.filter(p=>!inSquad.has(String(p.id)));
  },[squad, sharedSquad]);
  const dbFiltered = useMemo(()=>{
    if(!dbSearch) return allAvailablePlayers.slice(0,12);
    return allAvailablePlayers.filter(p=>
      p.name.toLowerCase().includes(dbSearch.toLowerCase())||
      p.pos.toLowerCase().includes(dbSearch.toLowerCase())||
      (p.club||"").toLowerCase().includes(dbSearch.toLowerCase())
    ).slice(0,12);
  },[allAvailablePlayers,dbSearch]);

  const addPlayerToSquad = (player) => {
    if(squad.find(p=>p.id===player.id)) return;
    const newSquad = [...squad, {...player,squadNum:squad.length+1}];
    setSquad(newSquad);
    if(selectedPitchToken){
      setPitchTokens(prev=>prev.map(t=>t.id===selectedPitchToken?{...t,player:{...player,squadNum:newSquad.length}}:t));
      setSelectedPitchToken(null);
    }
    setShowAddModal(false);
    toast && toast(`${player.name} added to squad`, "success");
    setNewPlayer({name:"",pos:"ST",style:"Clinical Finisher",proRef:"",age:22,nationality:"English",club:"",potential:75,pace:75,shooting:75,passing:75,dribbling:75,defending:50,physical:75});
  };

  const saveNewPlayer = () => {
    if(!newPlayer.name) return;
    const overall = Math.round((newPlayer.pace+newPlayer.shooting+newPlayer.passing+newPlayer.dribbling+newPlayer.defending+newPlayer.physical)/6);
    addPlayerToSquad({
      id:Date.now(),
      name:newPlayer.name, pos:newPlayer.pos, group:posGroup(newPlayer.pos),
      style:newPlayer.style, proRef:newPlayer.proRef||"",
      age:Number(newPlayer.age)||22,
      nationality:newPlayer.nationality||"English",
      club:newPlayer.club||"",
      potential:Number(newPlayer.potential)||overall,
      pace:newPlayer.pace, shooting:newPlayer.shooting, passing:newPlayer.passing,
      dribbling:newPlayer.dribbling, defending:newPlayer.defending, physical:newPlayer.physical,
      overall, availability:"Available", morale:"Good",
    });
  };

  const saveEditPlayer = () => {
    if(!editingPlayer) return;
    const overall = Math.round((editingPlayer.pace+editingPlayer.shooting+editingPlayer.passing+editingPlayer.dribbling+editingPlayer.defending+editingPlayer.physical)/6);
    const updated = squad.map(p=>p.id===editingPlayer.id ? {...editingPlayer, overall, group:posGroup(editingPlayer.pos)} : p);
    setSquad(updated);
    if(onSquadChange) onSquadChange(updated);
    saveToStorage('my_squad', updated);
    setEditingPlayer(null);
    toast && toast(`${editingPlayer.name} updated`, "success");
  };

  const removePlayer = (id) => {
    const p = squad.find(x=>x.id===id);
    setSquad(prev=>prev.filter(p=>p.id!==id));
    toast && toast(`${p?.name||"Player"} removed from squad`, "info");
  };

  const squadOverall = squad.length>0?Math.round(squad.reduce((s,p)=>s+p.overall,0)/squad.length):0;
  const attackers = squad.filter(p=>p.group==="att").length;
  const midfielders = squad.filter(p=>p.group==="mid").length;
  const defenders = squad.filter(p=>["def","gk"].includes(p.group)).length;

  /* Match slot to player */
  const getSlotPlayer = (slot) => squad.find(p=>p.pos===slot.pos)||null;

  const weekPlan = MATCH_WEEK_TEMPLATES[planSystem]||MATCH_WEEK_TEMPLATES["city433"];
  const oppData = oppType?OPP_PREP[oppType]:null;

  return(
    <>
      <style>{SC_CSS}</style>

      {/* ADD PLAYER MODAL */}
      {showAddModal&&(
        <div className="add-modal">
          <div className="add-modal-card">
            <div className="add-modal-header">
              <div className="add-modal-title">Add Player to Squad</div>
              <button className="add-modal-close" onClick={()=>setShowAddModal(false)}>✕ Close</button>
            </div>
            <div className="add-modal-body">
              <div className="add-tabs">
                <div className={`add-tab ${addTab==="new"?"on":""}`} onClick={()=>setAddTab("new")}>✏️ Add New Player</div>
                <div className={`add-tab ${addTab==="db"?"on":""}`} onClick={()=>setAddTab("db")}>👤 From My Squad</div>
              </div>

              {addTab==="new"&&(
                <>
                  <label className="sc-label">Full Name *</label>
                  <input className="sc-input" placeholder="e.g. James Richardson" value={newPlayer.name} onChange={e=>setNewPlayer(p=>({...p,name:e.target.value}))} autoFocus/>

                  <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.8rem"}}>
                    <div>
                      <label className="sc-label">Position *</label>
                      <select className="sc-select" value={newPlayer.pos} onChange={e=>setNewPlayer(p=>({...p,pos:e.target.value}))}>
                        {["GK","CB","RB","LB","CDM","CM","CAM","LW","RW","ST"].map(p=><option key={p}>{p}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="sc-label">Age</label>
                      <input className="sc-input" type="number" min="15" max="45" placeholder="22" value={newPlayer.age} onChange={e=>setNewPlayer(p=>({...p,age:e.target.value}))}/>
                    </div>
                    <div>
                      <label className="sc-label">Current Club / Team</label>
                      <input className="sc-input" placeholder="e.g. Town FC, Free agent" value={newPlayer.club} onChange={e=>setNewPlayer(p=>({...p,club:e.target.value}))}/>
                    </div>
                    <div>
                      <label className="sc-label">Nationality</label>
                      <input className="sc-input" placeholder="e.g. English, Irish" value={newPlayer.nationality} onChange={e=>setNewPlayer(p=>({...p,nationality:e.target.value}))}/>
                    </div>
                    <div>
                      <label className="sc-label">Playing Style</label>
                      <select className="sc-select" value={newPlayer.style} onChange={e=>setNewPlayer(p=>({...p,style:e.target.value}))}>
                        {PLAYER_STYLES.map(s=><option key={s}>{s}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="sc-label">Potential Rating (1–99)</label>
                      <input className="sc-input" type="number" min="1" max="99" placeholder="75" value={newPlayer.potential} onChange={e=>setNewPlayer(p=>({...p,potential:Number(e.target.value)}))}/>
                    </div>
                  </div>

                  <label className="sc-label" style={{marginTop:"0.75rem"}}>Plays Like (Pro Reference — optional)</label>
                  <select className="sc-select" value={newPlayer.proRef} onChange={e=>setNewPlayer(p=>({...p,proRef:e.target.value}))}>
                    <option value="">— Select a pro player they resemble —</option>
                    {PRO_PLAYER_REFS.map(p=><option key={p}>{p}</option>)}
                  </select>

                  <div className="attr-sliders" style={{marginTop:"0.85rem"}}>
                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.5rem"}}>
                      Attribute Ratings (drag to set) · Overall: <strong style={{color:"var(--teal)"}}>{Math.round((newPlayer.pace+newPlayer.shooting+newPlayer.passing+newPlayer.dribbling+newPlayer.defending+newPlayer.physical)/6)}</strong>
                    </div>
                    {["pace","shooting","passing","dribbling","defending","physical"].map(attr=>(
                      <div key={attr} className="attr-slide">
                        <label className="attr-slide-label">
                          <span>{attr.toUpperCase()}</span>
                          <span className={`attr-slide-val ${attrC(newPlayer[attr])}`}>{newPlayer[attr]}</span>
                        </label>
                        <input className="attr-range" type="range" min="30" max="99" value={newPlayer[attr]} onChange={e=>setNewPlayer(p=>({...p,[attr]:Number(e.target.value)}))}/>
                      </div>
                    ))}
                  </div>

                  <button className="btn-save-player" onClick={saveNewPlayer} disabled={!newPlayer.name}>
                    Add {newPlayer.name||"Player"} to Squad →
                  </button>
                </>
              )}

              {addTab==="db"&&(
                <div className="db-search-modal">
                  <input className="db-search-input" placeholder="Search your squad by name, position or club..." value={dbSearch} onChange={e=>setDbSearch(e.target.value)}/>
                  {dbFiltered.length===0?(
                    <div style={{textAlign:"center",padding:"2rem",color:"var(--muted)",fontStyle:"italic",fontSize:"0.82rem"}}>
                      {allAvailablePlayers.length===0
                        ? "All your squad players are already added. Use Add New Player to create a new one."
                        : "No players found matching your search."}
                    </div>
                  ):dbFiltered.map(p=>(
                    <div key={p.id} className="db-result-row" onClick={()=>addPlayerToSquad({...p,style:p.style||"Box-to-Box CM",proRef:""})}>
                      <span className={`sp-pos-badge ${p.group||posGroup(p.pos)}`}>{p.pos}</span>
                      <div style={{flex:1}}>
                        <div className="db-result-name">{p.name}</div>
                        <div className="db-result-meta">{p.nat} · {p.club} · Age {p.age}</div>
                      </div>
                      <div className="db-result-ovr">{p.overall}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* EDIT PLAYER MODAL */}
      {editingPlayer&&(
        <div className="add-modal">
          <div className="add-modal-card">
            <div className="add-modal-header">
              <div className="add-modal-title">Edit — {editingPlayer.name}</div>
              <button className="add-modal-close" onClick={()=>setEditingPlayer(null)}>✕ Close</button>
            </div>
            <div className="add-modal-body">
              <label className="sc-label">Full Name</label>
              <input className="sc-input" value={editingPlayer.name} onChange={e=>setEditingPlayer(p=>({...p,name:e.target.value}))}/>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.8rem"}}>
                <div>
                  <label className="sc-label">Position</label>
                  <select className="sc-select" value={editingPlayer.pos} onChange={e=>setEditingPlayer(p=>({...p,pos:e.target.value}))}>
                    {["GK","CB","RB","LB","CDM","CM","CAM","LW","RW","ST"].map(p=><option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="sc-label">Age</label>
                  <input className="sc-input" type="number" min="15" max="45" value={editingPlayer.age||""} onChange={e=>setEditingPlayer(p=>({...p,age:Number(e.target.value)}))}/>
                </div>
                <div>
                  <label className="sc-label">Club / Team</label>
                  <input className="sc-input" value={editingPlayer.club||""} onChange={e=>setEditingPlayer(p=>({...p,club:e.target.value}))} placeholder="Free agent, Town FC..."/>
                </div>
                <div>
                  <label className="sc-label">Nationality</label>
                  <input className="sc-input" value={editingPlayer.nationality||editingPlayer.nat||""} onChange={e=>setEditingPlayer(p=>({...p,nationality:e.target.value}))} placeholder="English..."/>
                </div>
                <div>
                  <label className="sc-label">Playing Style</label>
                  <select className="sc-select" value={editingPlayer.style||""} onChange={e=>setEditingPlayer(p=>({...p,style:e.target.value}))}>
                    {PLAYER_STYLES.map(s=><option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="sc-label">Potential</label>
                  <input className="sc-input" type="number" min="1" max="99" value={editingPlayer.potential||editingPlayer.overall||75} onChange={e=>setEditingPlayer(p=>({...p,potential:Number(e.target.value)}))}/>
                </div>
              </div>
              <div className="attr-sliders" style={{marginTop:"0.75rem"}}>
                <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--muted)",marginBottom:"0.5rem"}}>
                  Attributes · New Overall: <strong style={{color:"var(--teal)"}}>{Math.round(((editingPlayer.pace||70)+(editingPlayer.shooting||70)+(editingPlayer.passing||70)+(editingPlayer.dribbling||70)+(editingPlayer.defending||50)+(editingPlayer.physical||70))/6)}</strong>
                </div>
                {["pace","shooting","passing","dribbling","defending","physical"].map(attr=>(
                  <div key={attr} className="attr-slide">
                    <label className="attr-slide-label">
                      <span>{attr.toUpperCase()}</span>
                      <span className={`attr-slide-val ${attrC(editingPlayer[attr]||70)}`}>{editingPlayer[attr]||70}</span>
                    </label>
                    <input className="attr-range" type="range" min="30" max="99" value={editingPlayer[attr]||70} onChange={e=>setEditingPlayer(p=>({...p,[attr]:Number(e.target.value)}))}/>
                  </div>
                ))}
              </div>
              <div style={{display:"flex",gap:"0.6rem",marginTop:"0.5rem"}}>
                <button className="btn-save-player" onClick={saveEditPlayer} style={{flex:1}}>Save Changes →</button>
                <button onClick={()=>{removePlayer(editingPlayer.id);setEditingPlayer(null);}} style={{padding:"0.68rem 1rem",background:"transparent",color:"var(--darkred)",border:"1.5px solid var(--darkred)",fontFamily:"'DM Sans',sans-serif",fontWeight:800,fontSize:"0.68rem",letterSpacing:"1.5px",textTransform:"uppercase",cursor:"pointer"}}>Remove</button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="sc-wrap">
        {/* HERO */}
        <div className="sc-hero">
          <div className="sc-hero-inner">
            <div>
              <div className="sc-hero-title">Squad Coaching Platform</div>
              <div className="sc-hero-sub">Build your squad, get individual development plans, generate match week training sessions and prepare for any opponent type.</div>
            </div>
            <div className="sc-hero-stats">
              <div className="sc-stat"><div className="sc-stat-n">{squad.length}</div><div className="sc-stat-l">Players</div></div>
              <div className="sc-stat"><div className="sc-stat-n">{squadOverall||"—"}</div><div className="sc-stat-l">Avg Rating</div></div>
              <div className="sc-stat"><div className="sc-stat-n">{formation}</div><div className="sc-stat-l">Formation</div></div>
            </div>
          </div>
        </div>

        {/* MODULE TABS */}
        <div className="sc-tabs">
          <div className={`sc-tab ${activeTab==="squad"?"on":""}`} onClick={()=>setActiveTab("squad")}>🏟️ My Squad {squad.length>0&&<span className="sc-tab-badge">{squad.length}</span>}</div>
          <div className={`sc-tab ${activeTab==="profiles"?"on":""}`} onClick={()=>setActiveTab("profiles")}>📋 Player Profiles</div>
          <div className={`sc-tab ${activeTab==="individual"?"on":""}`} onClick={()=>setActiveTab("individual")}>👤 Individual Development</div>
          <div className={`sc-tab ${activeTab==="planner"?"on":""}`} onClick={()=>setActiveTab("planner")}>
            📅 Training Planner
            {sessionDrills.length>0&&<span className="sc-tab-badge">{sessionDrills.length}</span>}
          </div>
          <div className={`sc-tab ${activeTab==="opposition"?"on":""}`} onClick={()=>setActiveTab("opposition")}>🎯 Opposition Prep</div>
        </div>

        <div className="sc-content">

          {/* ══ MODULE 1: SQUAD BUILDER ══ */}
          {activeTab==="squad"&&(
            <>
              <div className="squad-layout">
                {/* PITCH */}
                <div>
                  <div className="squad-pitch-wrap">
                    <div className="squad-pitch-header">
                      <div className="squad-pitch-title">Formation — {formation}</div>
                      <div style={{display:"flex",alignItems:"center",gap:"0.75rem"}}>
                        {squad.length>0&&(
                          <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:700,color:"var(--mustard)",letterSpacing:"1px",display:"flex",alignItems:"center",gap:"0.3rem"}}>
                            <div style={{width:6,height:6,borderRadius:"50%",background:"#4ade80"}}/>
                            LIVE — {squad.length} players synced
                          </div>
                        )}
                        <select className="formation-select" value={formation} onChange={e=>handleFormationChange(e.target.value)}>
                          {Object.keys(FORMATION_SLOTS).map(f=><option key={f}>{f}</option>)}
                        </select>
                      </div>
                    </div>
                    <InteractivePitch
                      pitchTokens={pitchTokens}
                      setPitchTokens={setPitchTokens}
                      selectedPitchToken={selectedPitchToken}
                      setSelectedPitchToken={setSelectedPitchToken}
                      onEmptySlotClick={()=>setShowAddModal(true)}
                      onTokenClick={(tok)=>{setSelectedPlayer(tok.player);}}
                    />
                  </div>

                  {/* Pitch token quick info */}
                  {selectedPitchToken&&(()=>{
                    const tok = pitchTokens.find(t=>t.id===selectedPitchToken);
                    if(!tok) return null;
                    return(
                      <div style={{background:"var(--teal4)",border:"1.5px solid var(--teal)",borderTop:"2px solid var(--mustard)",padding:"0.75rem 1rem",display:"flex",alignItems:"center",gap:"0.8rem",flexWrap:"wrap"}}>
                        <div style={{flex:1}}>
                          <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--teal)",marginBottom:"0.2rem"}}>{tok.player?"Selected Player":"Empty Slot"}</div>
                          <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.88rem",fontWeight:800,color:"var(--ink)"}}>{tok.player?.name||tok.pos}</div>
                          {tok.player&&<div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",color:"var(--muted)",fontStyle:"italic"}}>{tok.player.style} · OVR {tok.player.overall}</div>}
                        </div>
                        {tok.player&&(
                          <div className="fm-attrs" style={{flex:"0 0 auto"}}>
                            {[["PAC",tok.player.pace],["SHO",tok.player.shooting],["PAS",tok.player.passing],["DRI",tok.player.dribbling],["DEF",tok.player.defending],["PHY",tok.player.physical]].map(([k,v])=>(
                              <div key={k} className="fma"><span className="fma-l">{k}</span><span className={`fma-v ${v>=80?"av-hi":v>=65?"av-md":"av-lo"}`}>{v}</span></div>
                            ))}
                          </div>
                        )}
                        <div style={{display:"flex",gap:"0.4rem",flexShrink:0}}>
                          {tok.player&&(
                            <button style={{padding:"0.42rem 0.85rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",background:"transparent",color:"var(--darkred)",border:"1.5px solid var(--darkred)",cursor:"pointer"}}
                              onClick={()=>{setPitchTokens(prev=>prev.map(t=>t.id===selectedPitchToken?{...t,player:null}:t));setSelectedPitchToken(null);}}>
                              Remove from Pitch
                            </button>
                          )}
                          {!tok.player&&(
                            <button style={{padding:"0.42rem 0.85rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",background:"var(--teal)",color:"var(--white)",border:"none",cursor:"pointer"}}
                              onClick={()=>setShowAddModal(true)}>
                              + Add Player
                            </button>
                          )}
                          <button style={{padding:"0.42rem 0.85rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",background:"transparent",color:"var(--muted)",border:"1.5px solid var(--rule)",cursor:"pointer"}}
                            onClick={()=>setSelectedPitchToken(null)}>
                            Done
                          </button>
                        </div>
                      </div>
                    );
                  })()}
                  {squad.length>=5&&(
                    <div className="squad-analysis">
                      <div className="squad-analysis-title">Squad Analysis <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",fontWeight:700,color:"var(--muted)",letterSpacing:"1px",fontStyle:"italic"}}>{squad.length} players</span></div>
                      <div className="squad-analysis-grid">
                        <div className="sqa-card"><div className="sqa-val">{squadOverall}</div><div className="sqa-lbl">Avg Overall</div></div>
                        <div className="sqa-card"><div className="sqa-val">{attackers}</div><div className="sqa-lbl">Attackers</div></div>
                        <div className="sqa-card"><div className="sqa-val">{midfielders}</div><div className="sqa-lbl">Midfielders</div></div>
                        <div className="sqa-card"><div className="sqa-val">{defenders}</div><div className="sqa-lbl">Defenders</div></div>
                      </div>
                      <div className="squad-system-match">
                        <div className="ssm-pct">{(()=>{
                          // Real system fit score based on squad's actual attribute profile
                          // Each system rewards specific attribute combinations
                          const systemWeights = {
                            city433:    {passing:1.0,dribbling:0.9,pace:0.6,shooting:0.6,defending:0.5,physical:0.5},
                            liverpool423:{pace:0.9,physical:0.9,pressing:0.0,passing:0.7,dribbling:0.6,shooting:0.7,defending:0.6},
                            arsenal433: {passing:0.9,pace:0.8,dribbling:0.8,shooting:0.7,defending:0.6,physical:0.6},
                            atletico442:{defending:1.0,physical:1.0,pace:0.6,shooting:0.7,passing:0.5,dribbling:0.4},
                            leverkusen3421:{passing:0.9,pace:0.7,dribbling:0.8,defending:0.7,physical:0.7,shooting:0.6},
                            chelsea343: {defending:0.9,physical:0.8,pace:0.8,passing:0.7,dribbling:0.7,shooting:0.6},
                            bielsa4141: {pace:0.9,physical:0.95,defending:0.8,passing:0.6,dribbling:0.6,shooting:0.6},
                            mourinho4231:{defending:0.95,physical:0.9,pace:0.7,passing:0.7,shooting:0.7,dribbling:0.5},
                          };
                          const w = systemWeights[planSystem] || systemWeights.city433;
                          const attrs = ["pace","shooting","passing","dribbling","defending","physical"];
                          const avgAttrs = {};
                          attrs.forEach(a => {
                            avgAttrs[a] = squad.length > 0
                              ? Math.round(squad.reduce((s,p)=>s+(p[a]||70),0)/squad.length)
                              : 70;
                          });
                          let score = 0, totalW = 0;
                          attrs.forEach(a => {
                            const weight = w[a] || 0.5;
                            const norm = (avgAttrs[a] - 30) / 69; // 0–1
                            score += norm * weight;
                            totalW += weight;
                          });
                          return Math.min(97, Math.max(42, Math.round((score/totalW)*100)));
                        })()}%</div>
                        <div className="ssm-info">
                          <div className="ssm-system">Best System Match: {TACTICAL_SYSTEMS_SHORT.find(s=>s.id===planSystem)?.name||"4-3-3 Positional Play"}</div>
                          <div className="ssm-reason">Based on your squad's attribute profile, playing styles and position distribution.</div>
                          <div style={{display:"flex",gap:"0.5rem",marginTop:"0.7rem"}}>
                            <button style={{flex:1,padding:"0.45rem",background:"var(--teal)",color:"var(--white)",border:"none",fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",cursor:"pointer"}}
                              onClick={()=>{saveToStorage('my_squad',squad);if(onSquadChange)onSquadChange(squad);if(onNavigate)onNavigate("tacticsboard");}}>
                              🎨 Open Tactics Board
                            </button>
                            <button style={{flex:1,padding:"0.45rem",background:"transparent",color:"var(--teal)",border:"1.5px solid var(--teal)",fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",cursor:"pointer"}}
                              onClick={()=>setActiveTab("planner")}>
                              📅 Training Planner
                            </button>
                          </div>
                        </div>
                      </div>
                      <div style={{marginTop:"0.85rem"}}>
                        <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--teal)",marginBottom:"0.5rem"}}>Squad Strengths</div>
                        {squad.slice(0,3).map((p,i)=>(
                          <div key={i} style={{display:"flex",alignItems:"center",gap:"0.6rem",padding:"0.42rem 0",borderBottom:"1px solid var(--rule2)"}}>
                            <span className={`sp-pos-badge ${p.group}`}>{p.pos}</span>
                            <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",fontWeight:700,flex:1}}>{p.name}</span>
                            <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",color:"var(--teal)",fontStyle:"italic"}}>{p.style}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* SQUAD PANEL */}
                <div className="squad-panel">
                  <div className="squad-panel-header">
                    <div>
                      <div className="squad-panel-title">Squad ({squad.length}/40)</div>
                      {squad.length>0&&<div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",color:"var(--muted)",fontStyle:"italic",marginTop:"0.08rem"}}>⠿ Drag players onto the pitch</div>}
                    </div>
                    <div style={{display:"flex",gap:"0.4rem"}}>
                      {squad.length>=3&&(
                        <button style={{padding:"0.38rem 0.85rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",background:"var(--mustard)",color:"var(--ink)",border:"none",cursor:"pointer"}}
                          onClick={()=>{
                            saveToStorage('my_squad', squad);
                            if(onSquadChange) onSquadChange(squad);
                            if(onNavigate) onNavigate("tacticsboard");
                          }}>
                          🎨 To Board
                        </button>
                      )}
                      {squad.length>0&&<ExportButton onClick={()=>exportSquadPDF(squad,loadFromStorage("fx_club_name","My Club")||"My Club")} label="Print Squad" small/>}
                      <button className="btn-add-player" onClick={()=>setShowAddModal(true)}>+ Add Player</button>
                    </div>
                  </div>
                  {squad.length===0?(
                    <div className="sc-empty" style={{padding:"2.5rem 1.5rem"}}>
                      <div className="sc-empty-icon">🏟️</div>
                      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.2rem",letterSpacing:"2px",marginBottom:"0.4rem"}}>Squad is Empty</div>
                      <div style={{fontSize:"0.78rem",color:"var(--muted)",fontStyle:"italic",fontWeight:300,lineHeight:1.6}}>Add your players — type new ones or pick from the player database.</div>
                      <button className="btn-primary" style={{marginTop:"1rem",fontSize:"0.72rem",padding:"0.7rem 1.5rem"}} onClick={()=>setShowAddModal(true)}>Add First Player</button>
                    </div>
                  ):squad.map((p,i)=>(
                    <div key={p.id}
                      className={`squad-player-row ${selectedPlayer?.id===p.id?"selected":""}`}
                      draggable
                      onDragStart={e=>{
                        e.dataTransfer.setData("squad_player",JSON.stringify(p));
                        e.currentTarget.style.opacity="0.5";
                      }}
                      onDragEnd={e=>{e.currentTarget.style.opacity="1";}}
                      onClick={()=>setSelectedPlayer(selectedPlayer?.id===p.id?null:p)}>
                      <div className="sp-num">{i+1}</div>
                      <div className="sp-av">{ini(p.name)}</div>
                      <div className="sp-info">
                        <div className="sp-name">{p.name}</div>
                        <div className="sp-meta">
                          {p.age&&<span>Age {p.age} · </span>}
                          {p.club&&<span>{p.club} · </span>}
                          {p.style}
                        </div>
                      </div>
                      <span className={`sp-pos-badge ${p.group}`}>{p.pos}</span>
                      <div className="sp-ovr">{p.overall}</div>
                      <button style={{background:"transparent",border:"1px solid var(--rule)",color:"var(--muted)",cursor:"pointer",fontSize:"0.6rem",fontWeight:700,padding:"0.18rem 0.45rem",letterSpacing:"1px",textTransform:"uppercase",fontFamily:"'DM Sans',sans-serif"}} onClick={e=>{e.stopPropagation();setEditingPlayer({...p});}}>Edit</button>
                      <button style={{background:"transparent",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:"0.75rem",padding:"0.2rem 0.4rem"}} onClick={e=>{e.stopPropagation();removePlayer(p.id);}}>✕</button>
                    </div>
                  ))}
                  {selectedPlayer&&(
                    <div style={{padding:"0.85rem 1rem",background:"var(--teal4)",borderTop:"1.5px solid var(--teal)"}}>
                      <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--teal)",marginBottom:"0.5rem"}}>Quick View — {selectedPlayer.name}</div>
                      <div className="fm-attrs">
                        {[["PAC",selectedPlayer.pace],["SHO",selectedPlayer.shooting],["PAS",selectedPlayer.passing],["DRI",selectedPlayer.dribbling],["DEF",selectedPlayer.defending],["PHY",selectedPlayer.physical]].map(([k,v])=>(
                          <div key={k} className="fma"><span className="fma-l">{k}</span><span className={`fma-v ${attrC(v)}`}>{v}</span></div>
                        ))}
                      </div>
                      <button style={{width:"100%",marginTop:"0.65rem",padding:"0.5rem",background:"var(--teal)",color:"var(--white)",border:"none",fontFamily:"'DM Sans',sans-serif",fontSize:"0.68rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",cursor:"pointer"}} onClick={()=>{setDevPlayer(selectedPlayer);setActiveTab("individual");}}>
                        View Development Plan →
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </>
          )}

          {/* ══ MODULE 2: INDIVIDUAL DEVELOPMENT ══ */}
          {activeTab==="profiles"&&(
            <PlayerProfilesTab squad={squad} onNavigate={onNavigate}/>
          )}

          {activeTab==="individual"&&(
            squad.length===0?(
              <div className="sc-empty">
                <div className="sc-empty-icon">👤</div>
                <div className="sc-empty-title">No Squad Yet</div>
                <div className="sc-empty-sub">Add players to your squad first to generate individual development plans.</div>
                <button className="btn-primary" onClick={()=>setActiveTab("squad")}>Build My Squad →</button>
              </div>
            ):(
              <div className="dev-layout">
                {/* Player list */}
                <div className="dev-player-list">
                  <div className="dev-player-list-header">Select Player</div>
                  {squad.map(p=>(
                    <div key={p.id} className={`dev-player-item ${devPlayer?.id===p.id?"on":""}`} onClick={()=>setDevPlayer(p)}>
                      <div className="sp-av" style={{background:devPlayer?.id===p.id?"var(--mustard)":"var(--teal)"}}>{ini(p.name)}</div>
                      <div style={{flex:1}}>
                        <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.8rem",fontWeight:700,color:"var(--ink)"}}>{p.name}</div>
                        <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--muted)"}}>{p.pos} · {p.style}</div>
                      </div>
                      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1rem",letterSpacing:"1px",color:"var(--darkred)"}}>{p.overall}</div>
                    </div>
                  ))}
                </div>

                {/* Development detail */}
                {devPlayer?(
                  <div className="dev-player-detail">
                    <div className="dev-player-header">
                      <div>
                        <div className="dev-player-name">{devPlayer.name}</div>
                        <div className="dev-player-meta">{devPlayer.pos} · {devPlayer.style}{devPlayer.proRef?` · Plays like ${devPlayer.proRef}`:""}</div>
                      </div>
                      <div><div className="dev-ovr">{devPlayer.overall}</div><div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.52rem",color:"rgba(255,255,255,0.45)",letterSpacing:"2px",textTransform:"uppercase",textAlign:"right"}}>Overall</div></div>
                    </div>

                    <div className="dev-body">
                      {/* Attributes */}
                      <div className="dev-section-title">Attribute Profile <span className="dev-chip">FM Style</span></div>
                      <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"0.5rem",marginBottom:"1.1rem"}}>
                        {[["Pace",devPlayer.pace],["Shooting",devPlayer.shooting],["Passing",devPlayer.passing],["Dribbling",devPlayer.dribbling],["Defending",devPlayer.defending],["Physical",devPlayer.physical]].map(([k,v])=>(
                          <div key={k} style={{display:"flex",alignItems:"center",gap:"0.6rem"}}>
                            <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",fontWeight:700,color:"var(--muted)",textTransform:"uppercase",letterSpacing:"0.5px",width:"68px",flexShrink:0}}>{k}</span>
                            <div style={{flex:1,height:"6px",background:"var(--cream2)",borderRadius:"2px",position:"relative"}}>
                              <div style={{position:"absolute",top:0,left:0,height:"100%",width:`${v}%`,background:v>=80?"#16a34a":v>=65?"var(--mustard)":"var(--darkred)",borderRadius:"2px",opacity:0.8}}/>
                            </div>
                            <span style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.95rem",letterSpacing:"1px",color:v>=80?"#16a34a":v>=65?"var(--mustard2)":"var(--darkred)",width:"24px",textAlign:"right"}}>{v}</span>
                          </div>
                        ))}
                      </div>

                      {/* Pro comparison */}
                      {devPlayer.proRef&&(
                        <>
                          <div className="dev-section-title">Pro Comparison <span className="dev-chip">{devPlayer.proRef}</span></div>
                          <div className="pro-comparison">
                            <div className="pro-comp-card">
                              <div className="pro-comp-label">Your Player</div>
                              <div className="pro-comp-name">{devPlayer.name}</div>
                              <div className="pro-comp-attrs">
                                {[["PAC",devPlayer.pace],["SHO",devPlayer.shooting],["PAS",devPlayer.passing],["DRI",devPlayer.dribbling],["DEF",devPlayer.defending],["PHY",devPlayer.physical]].map(([k,v])=>(
                                  <div key={k} className="pca2"><span className="pca2-l">{k}</span><span className={`pca2-v ${attrC(v)}`}>{v}</span></div>
                                ))}
                              </div>
                            </div>
                            <div className="pro-comp-card" style={{background:"var(--teal4)",borderColor:"var(--teal)"}}>
                              <div className="pro-comp-label">Pro Reference</div>
                              <div className="pro-comp-name">{devPlayer.proRef}</div>
                              <div className="pro-comp-attrs">
                                {(()=>{const a=getProAttrs(devPlayer.proRef);return[["PAC",a.pace],["SHO",a.shooting],["PAS",a.passing],["DRI",a.dribbling],["DEF",a.defending],["PHY",a.physical]].map(([k,v])=>(
                                  <div key={k} className="pca2"><span className="pca2-l">{k}</span><span className={`pca2-v ${attrC(v)}`}>{v}</span></div>
                                ))})()}
                              </div>
                            </div>
                          </div>
                          <div style={{background:"var(--mustard3)",border:"1px solid var(--mustard)",padding:"0.75rem 0.9rem",marginBottom:"1.1rem"}}>
                            <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--mustard2)",marginBottom:"0.3rem"}}>Gap Analysis</div>
                            {(()=>{
                              const proA=getProAttrs(devPlayer.proRef);
                              const gaps=[
                                {k:"Pace",diff:proA.pace-devPlayer.pace},
                                {k:"Shooting",diff:proA.shooting-devPlayer.shooting},
                                {k:"Passing",diff:proA.passing-devPlayer.passing},
                                {k:"Dribbling",diff:proA.dribbling-devPlayer.dribbling},
                                {k:"Defending",diff:proA.defending-devPlayer.defending},
                                {k:"Physical",diff:proA.physical-devPlayer.physical},
                              ].filter(g=>g.diff>5).sort((a,b)=>b.diff-a.diff).slice(0,3);
                              return gaps.length>0?gaps.map((g,i)=>(
                                <div key={i} style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.75rem",color:"var(--ink2)",marginBottom:"0.2rem",display:"flex",gap:"0.5rem"}}>
                                  <span style={{color:"var(--darkred)",fontWeight:800}}>▲{g.diff}</span>
                                  <span>{g.k} — key development area to reach {devPlayer.proRef}'s level</span>
                                </div>
                              )):<div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.75rem",color:"var(--teal)",fontWeight:700}}>✦ Strong match to {devPlayer.proRef}'s profile — maintain current trajectory</div>;
                            })()}
                          </div>
                        </>
                      )}

                      {/* Individual drills */}
                      <div className="dev-section-title">Individual Development Drills <span className="dev-chip">{devPlayer.pos}</span></div>
                      {getDrillsForStyle(devPlayer.style).map((d,i)=>(
                        <div key={i} className="drill-card">
                          <div className="drill-card-title">{d.title}</div>
                          <div className="drill-card-desc">{d.desc}</div>
                          <div className="drill-card-meta">
                            <span className="drill-meta-tag duration">⏱ {d.duration}</span>
                            <span className="drill-meta-tag players">👥 {d.players}</span>
                            <span className="drill-meta-tag intensity">🔥 {d.intensity}</span>
                            <span className="drill-meta-tag focus">🎯 {d.focus}</span>
                          </div>
                        </div>
                      ))}

                      {/* Weekly programme */}
                      <div className="dev-section-title" style={{marginTop:"0.5rem"}}>10-Week Development Programme</div>
                      {[
                        {n:"Week 1-2",t:"Foundation",d:`Establish the basics of the ${devPlayer.style} role. Master the movement patterns and positional discipline without opposition.`},
                        {n:"Week 3-4",t:"Technical Quality",d:"Individual technique work — the specific skills required for the role. Finishing, passing range, dribbling or defensive positioning depending on the position."},
                        {n:"Week 5-6",t:"Press Resistance",d:"Introduce pressure. All technical drills now have a presser. Build confidence receiving and playing under physical challenge."},
                        {n:"Week 7-8",t:"Game Situations",d:"Apply the skills in game-realistic scenarios. Small-sided games with the role defined. Video analysis of each session."},
                        {n:"Week 9-10",t:"Match Integration",d:"Full 11v11 application. Specific coaching during the match to reinforce the development areas. Review against the pro reference profile."},
                      ].map((w,i)=>(
                        <div key={i} className="ck-dev-phase" style={{marginBottom:"0.55rem"}}>
                          <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--teal)"}}>{w.n}</div>
                          <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.82rem",fontWeight:800,color:"var(--ink)",marginBottom:"0.15rem"}}>{w.t}</div>
                          <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.73rem",color:"var(--muted)",fontStyle:"italic",fontWeight:300,lineHeight:1.6}}>{w.d}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                ):(
                  <div className="sc-empty">
                    <div className="sc-empty-icon">👈</div>
                    <div className="sc-empty-title">Select a Player</div>
                    <div className="sc-empty-sub">Choose a player from the list to view their individual development plan, pro comparison and personalised drills.</div>
                  </div>
                )}
              </div>
            )
          )}

          {/* ══ MODULE 3: TRAINING PLANNER ══ */}
          {activeTab==="planner"&&(
            <>
              {/* Saved drills from Knowledge Base */}
              {sessionDrills.length > 0 && (
                <div style={{background:"var(--white)",border:"1.5px solid var(--teal)",borderLeft:"4px solid var(--teal)",padding:"1.1rem",marginBottom:"1.2rem"}}>
                  <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:"0.8rem",flexWrap:"wrap",gap:"0.5rem"}}>
                    <div>
                      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.95rem",letterSpacing:"2px",color:"var(--ink)"}}>
                        Drills Saved from Knowledge Base
                        <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,padding:"0.12rem 0.45rem",background:"var(--teal)",color:"var(--white)",letterSpacing:"2px",textTransform:"uppercase",marginLeft:"0.6rem"}}>{sessionDrills.length}</span>
                      </div>
                      <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",color:"var(--muted)",fontStyle:"italic",fontWeight:300,marginTop:"0.1rem"}}>Click any drill to assign it to a session day below</div>
                    </div>
                    <button onClick={()=>{clearSessionDrills();refreshSessionDrills();}} style={{padding:"0.38rem 0.85rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",background:"transparent",color:"var(--darkred)",border:"1.5px solid var(--darkred)",cursor:"pointer"}}>
                      Clear All
                    </button>
                    <ExportButton onClick={()=>exportSessionPDF(sessionDrills,{title:"Training Session Plan"})} label="Print Session" small/>
                  </div>
                  <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(280px,1fr))",gap:"0.6rem"}}>
                    {sessionDrills.map((d,i)=>(
                      <div key={d.id||i} style={{background:"var(--teal4)",border:"1px solid var(--teal)",padding:"0.7rem 0.85rem",position:"relative"}}>
                        <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.8rem",fontWeight:800,color:"var(--ink)",marginBottom:"0.15rem",paddingRight:"1.2rem"}}>{d.title}</div>
                        {d.source&&<div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",color:"var(--teal)",fontWeight:700,letterSpacing:"1px",marginBottom:"0.2rem"}}>{d.source}</div>}
                        {d.meta&&<div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",color:"var(--muted)",fontStyle:"italic",fontWeight:300}}>{d.meta}</div>}
                        {d.desc&&<div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.7rem",color:"var(--ink2)",fontWeight:300,lineHeight:1.55,marginTop:"0.3rem",fontStyle:"italic"}}>{d.desc.slice(0,100)}{d.desc.length>100?"...":""}</div>}
                        <button
                          onClick={()=>{removeDrillFromSession(d.id);refreshSessionDrills();}}
                          style={{position:"absolute",top:"0.5rem",right:"0.5rem",background:"transparent",border:"none",color:"var(--muted)",cursor:"pointer",fontSize:"0.75rem",lineHeight:1}}
                        >✕</button>
                      </div>
                    ))}
                  </div>
                  <div style={{marginTop:"0.9rem",padding:"0.75rem 0.9rem",background:"var(--cream2)",border:"1px solid var(--rule)",display:"flex",alignItems:"center",gap:"0.6rem"}}>
                    <span style={{fontSize:"0.75rem",color:"var(--muted)",fontStyle:"italic",fontWeight:300,flex:1}}>These drills are included automatically in your generated match week plan below</span>
                    <button onClick={()=>{setPlanGenerated(true);}} style={{padding:"0.42rem 1rem",fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",fontWeight:800,letterSpacing:"1.5px",textTransform:"uppercase",background:"var(--teal)",color:"var(--white)",border:"none",cursor:"pointer"}}>
                      Build Session Plan →
                    </button>
                  </div>
                </div>
              )}

              <div className="planner-controls">
                <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1rem",letterSpacing:"2px",color:"var(--ink)",marginBottom:"0.4rem"}}>Generate Match Week Training Plan</div>
                <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",color:"var(--muted)",fontStyle:"italic",fontWeight:300,marginBottom:"1rem"}}>Select your tactical system and training phase to generate a full Tuesday–Friday session plan with detailed drills, timings and player tasks.</div>
                <div className="planner-controls-grid">
                  <div className="planner-field">
                    <label>Tactical System</label>
                    <select value={planSystem} onChange={e=>setPlanSystem(e.target.value)}>
                      {TACTICAL_SYSTEMS_SHORT.map(s=><option key={s.id} value={s.id}>{s.name} ({s.formation})</option>)}
                    </select>
                  </div>
                  <div className="planner-field">
                    <label>Training Phase</label>
                    <select value={planPhase} onChange={e=>setPlanPhase(e.target.value)}>
                      <option value="matchweek">Match Week (Tue–Fri)</option>
                      <option value="preseason">Pre-season Block</option>
                      <option value="recovery">Post-match Recovery Week</option>
                      <option value="intensive">Intensive Training Block</option>
                    </select>
                  </div>
                  <div className="planner-field">
                    <label>Squad Focus</label>
                    <select>
                      <option>Full Squad</option>
                      <option>Attacking Unit</option>
                      <option>Defensive Unit</option>
                      <option>Set Pieces</option>
                    </select>
                  </div>
                </div>
                <button className="btn-generate" onClick={()=>setPlanGenerated(true)}>Generate Full Match Week Plan →</button>
              </div>

              {planGenerated&&(
                <>
                  {/* Match week overview */}
                  <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1rem",letterSpacing:"2px",color:"var(--ink)",marginBottom:"0.85rem"}}>
                    Match Week Plan — {TACTICAL_SYSTEMS_SHORT.find(s=>s.id===planSystem)?.name}
                  </div>
                  <div className="match-week">
                    {["tue","wed","thu","fri"].map(day=>{
                      const d=weekPlan[day];
                      const intensityColors={"60":"#16a34a","85":"var(--darkred)","50":"#2e7d7a","70":"var(--mustard)"};
                      const intensityLabels={"60":"Light","85":"High","50":"Recovery","70":"Medium"};
                      return(
                        <div key={day} className="day-card">
                          <div className="day-header">
                            <div className="day-name">{day==="tue"?"Tuesday":day==="wed"?"Wednesday":day==="thu"?"Thursday":"Friday"}</div>
                            <div className="day-theme">{d.theme}</div>
                            <div className="day-duration">90 min session</div>
                          </div>
                          <div className="day-body">
                            <div className="day-section">
                              <div className="day-section-label">Session Drills</div>
                              {d.drills.map((dr,i)=>(
                                <div key={i} className="day-drill">
                                  {dr}
                                </div>
                              ))}
                            </div>
                            <div>
                              <div className="day-intensity">
                                {[1,2,3,4,5].map(n=>(
                                  <div key={n} className="day-intensity-bar">
                                    <div className="day-intensity-fill" style={{height:`${(d.intensity/100)*n*20<=d.intensity?"100":"0"}%`,background:intensityColors[String(d.intensity)]||"var(--teal)"}}/>
                                  </div>
                                ))}
                              </div>
                              <div className="day-intensity-label">Intensity: {intensityLabels[String(d.intensity)]||"Medium"} ({d.intensity}%)</div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* Detailed session plans */}
                  <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1rem",letterSpacing:"2px",color:"var(--ink)",marginTop:"1.5rem",marginBottom:"0.85rem"}}>Detailed Session Breakdown</div>
                  {["tue","wed","thu","fri"].map(day=>{
                    const d=weekPlan[day];
                    return(
                      <div key={day} className="session-detail" style={{marginBottom:"1rem"}}>
                        <div className="session-detail-header">
                          <div className="session-detail-title">{day==="tue"?"Tuesday":day==="wed"?"Wednesday":day==="thu"?"Thursday":"Friday"} — {d.theme}</div>
                          <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",color:"var(--muted)",fontStyle:"italic",fontWeight:300,marginTop:"0.2rem"}}>{d.detail}</div>
                        </div>
                        <div style={{padding:"0.9rem 1.1rem"}}>
                          {d.sessions.map((s,i)=>(
                            <div key={i} className="session-phase">
                              <div className="session-phase-time">{s.time}</div>
                              <div className="session-phase-content">
                                <div className="session-phase-title">{s.title}</div>
                                <div className="session-phase-desc">{s.desc}</div>
                                {s.players&&<div className="session-phase-players">👥 {s.players}</div>}
                              </div>
                            </div>
                          ))}
                          {/* Add squad-specific player tasks */}
                          {squad.length>0&&(
                            <div style={{marginTop:"0.85rem",padding:"0.75rem 0.9rem",background:"var(--teal4)",border:"1px solid var(--teal)"}}>
                              <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--teal)",marginBottom:"0.5rem"}}>Player-specific Tasks for {day==="tue"?"Tuesday":day==="wed"?"Wednesday":day==="thu"?"Thursday":"Friday"}</div>
                              {squad.slice(0,4).map((p,i)=>(
                                <div key={i} style={{display:"flex",alignItems:"center",gap:"0.6rem",padding:"0.38rem 0",borderBottom:"1px solid var(--rule2)"}}>
                                  <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",fontWeight:700,flex:1}}>{p.name}</span>
                                  <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.65rem",color:"var(--teal)",fontStyle:"italic"}}>
                                    {day==="tue"?"Focus on positional discipline and movement patterns":
                                     day==="wed"?"Press trigger work — activate the press at the correct moment":
                                     day==="thu"?"Set piece responsibility — know your exact role":
                                     "Execute the match plan — your specific attacking/defensive task"}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                          {/* Show saved Knowledge Base drills on Wednesday */}
                          {day==="wed"&&sessionDrills.length>0&&(
                            <div style={{marginTop:"0.85rem",padding:"0.75rem 0.9rem",background:"var(--mustard3)",border:"1px solid var(--mustard)"}}>
                              <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",fontWeight:800,letterSpacing:"2px",textTransform:"uppercase",color:"var(--mustard2)",marginBottom:"0.6rem"}}>
                                ✦ Drills from Knowledge Base ({sessionDrills.length})
                              </div>
                              {sessionDrills.map((d,i)=>(
                                <div key={i} style={{display:"flex",alignItems:"flex-start",gap:"0.6rem",padding:"0.42rem 0",borderBottom:"1px solid rgba(212,148,26,0.2)"}}>
                                  <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.7rem",color:"var(--mustard2)",fontWeight:800,flexShrink:0}}>→</div>
                                  <div style={{flex:1}}>
                                    <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.78rem",fontWeight:700,color:"var(--ink)"}}>{d.title}</div>
                                    {d.meta&&<div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.62rem",color:"var(--muted)",fontStyle:"italic",marginTop:"0.08rem"}}>{d.meta}</div>}
                                    {d.source&&<div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.58rem",color:"var(--mustard2)",fontWeight:700,letterSpacing:"1px",marginTop:"0.08rem"}}>{d.source}</div>}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </>
          )}

          {/* ══ MODULE 4: OPPOSITION PREP ══ */}
          {activeTab==="opposition"&&(
            <div className="opp-layout">
              {/* Selector */}
              <div>
                <div className="opp-selector">
                  <div className="opp-title">Select Opposition Type</div>
                  <div className="opp-sub">Choose the style of team you're facing this week. The system will generate a specific preparation plan — drills, shape work, player tasks and a day-by-day training schedule.</div>
                  <div className="opp-types">
                    {OPPOSITION_TYPES.map(o=>(
                      <button key={o.id} className={`opp-type-btn ${oppType===o.id?"on":""}`} onClick={()=>{setOppType(o.id);setOppPlanGenerated(false);}}>
                        <div className="opp-type-icon">{o.icon}</div>
                        <div style={{flex:1}}>
                          <div className="opp-type-name">{o.name}</div>
                          <div className="opp-type-desc">{o.desc}</div>
                          <div className="opp-examples">e.g. {o.examples}</div>
                        </div>
                      </button>
                    ))}
                  </div>
                  <button className="btn-prep" onClick={()=>{if(oppType)setOppPlanGenerated(true);}}>Generate Preparation Plan →</button>
                </div>
              </div>

              {/* Prep plan */}
              <div>
                {oppPlanGenerated&&oppData?(
                  <div className="prep-plan">
                    <div style={{background:"var(--darkred)",padding:"1rem 1.2rem",borderBottom:"2px solid var(--mustard)"}}>
                      <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"1.2rem",letterSpacing:"2px",color:"var(--white)"}}>{oppData.title}</div>
                      <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",color:"rgba(255,255,255,0.6)",fontStyle:"italic",fontWeight:300,marginTop:"0.2rem",lineHeight:1.6}}>{oppData.threat}</div>
                    </div>

                    <div className="prep-section">
                      <div className="prep-section-header"><div className="prep-section-icon">🧠</div><div className="prep-section-title">Key Principles for This Match</div></div>
                      <div className="prep-section-body">
                        {oppData.keyPrinciples.map((p,i)=><div key={i} className="prep-item">{p}</div>)}
                      </div>
                    </div>

                    <div className="prep-section">
                      <div className="prep-section-header"><div className="prep-section-icon">⚽</div><div className="prep-section-title">Preparation Drills</div></div>
                      <div className="prep-section-body">
                        {oppData.drills.map((d,i)=>(
                          <div key={i} className="prep-drill-item">
                            <div className="prep-drill-title">{d.title} <span style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.6rem",color:"var(--muted)",fontStyle:"italic",fontWeight:300}}>— {d.duration} · {d.players}</span></div>
                            <div className="prep-drill-desc">{d.desc}</div>
                          </div>
                        ))}
                      </div>
                    </div>

                    {squad.length>0&&(
                      <div className="prep-section">
                        <div className="prep-section-header"><div className="prep-section-icon">👥</div><div className="prep-section-title">Player-specific Focus ({squad.length} players)</div></div>
                        <div className="prep-section-body">
                          {squad.map((p,i)=>(
                            <div key={i} className="prep-player-focus">
                              <span className={`sp-pos-badge ${p.group}`}>{p.pos}</span>
                              <div className="prep-player-name">{p.name}</div>
                              <div className="prep-player-task">
                                {oppData.playerFocusTasks[i%oppData.playerFocusTasks.length].split(":")[1]?.trim()||oppData.playerFocusTasks[i%oppData.playerFocusTasks.length]}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    <div className="prep-section">
                      <div className="prep-section-header"><div className="prep-section-icon">📅</div><div className="prep-section-title">Match Week Schedule</div></div>
                      <div className="prep-section-body">
                        {oppData.weekPlan.map((w,i)=>(
                          <div key={i} style={{display:"flex",gap:"0.85rem",padding:"0.65rem 0",borderBottom:"1px solid var(--rule2)"}}>
                            <div style={{fontFamily:"'Bebas Neue',sans-serif",fontSize:"0.85rem",letterSpacing:"1.5px",color:"var(--darkred)",width:"80px",flexShrink:0}}>{w.day}</div>
                            <div>
                              <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.8rem",fontWeight:800,color:"var(--ink)",marginBottom:"0.15rem"}}>{w.focus}</div>
                              <div style={{fontFamily:"'DM Sans',sans-serif",fontSize:"0.72rem",color:"var(--muted)",fontStyle:"italic",fontWeight:300,lineHeight:1.6}}>{w.session}</div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ):(
                  <div className="sc-empty">
                    <div className="sc-empty-icon">🎯</div>
                    <div className="sc-empty-title">Select Opponent Type</div>
                    <div className="sc-empty-sub">Choose the style of team you're facing to generate a specific preparation plan with drills, principles and a match week schedule.</div>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>
      </div>
    </>
  );
}
